/**
 * CombatManager.js - D&D 5e Combat System Engine
 * 
 * Implements D&D 5e (2024) combat mechanics with full fidelity:
 * - Attack rolls with advantage/disadvantage, critical hits (nat 20), critical failures (nat 1)
 * - Damage calculation with proper critical hit rules (double dice, not modifiers)
 * - AC calculation with armor, shield, cover bonuses
 * - Range validation (normal/long range, disadvantage at long range)
 * - Reaction system hooks for Luck feat, Portent, etc.
 * - Detailed combat log with step-by-step resolution
 * - Deterministic and seedable for consistent results
 * 
 * D&D 5e Rules References:
 * - Attack Rolls: PHB p.194-196
 * - Damage and Critical Hits: PHB p.196-197
 * - Cover: PHB p.196
 * - Range: PHB p.147
 * 
 * date_of_change: 2025-08-31 - Enhanced with full D&D 5e combat mechanics and reaction system
 */

import RangeCalculator from './RangeCalculator.js';
import CombatLogManager from './CombatLogManager.js';
import { ConditionHandler } from '../handlers/ConditionHandler';
import { getDiceAuto, requestChoice } from './DiceProvider.js';
// Removed DND_CONSTANTS import - grid context must be passed explicitly by callers (policy: no defaults)
import { createUnifiedHP, updateEntityHP } from './HPFormatBridge.js';
import { normalizeAreaOfEffect } from './areaOfEffectUtils';
import { DEBUG_ENABLED } from './DebugLogger';
import {
  inferWeaponAttackType,
  inferWeaponDamageEntries,
  inferWeaponMagicBonus,
  inferWeaponRange,
  inferWeaponReach,
  mergeWeaponBaseDamageEntries
} from './combat/WeaponDamageInference.js';
import {
  areCombatAllies,
  areCombatEnemies,
  isCombatantIncapacitated,
  sameCombatEntity
} from './combat/CombatRelationships.js';
import { groupCreatureDamageDetails } from '../../../shared/rules/creatureDamage';
import {
  hasNamedCombatFeature,
  normalizeCunningStrikeSelection,
  resolveCunningStrikeSaveDC
} from './combat/RogueCombatFeatures.js';

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

const asRecord = (value) => (
  value && typeof value === 'object' ? value : null
);

const isFiniteNumber = (value) => (
  typeof value === 'number' && Number.isFinite(value)
);

const isNonEmptyString = (value) => (
  typeof value === 'string' && value.trim().length > 0
);

const CORE_ATTACK_RULES = Object.freeze({
  attackRollModifier: 0,
  damageRollModifier: 0,
  naturalOneAlwaysMisses: true,
  naturalTwentyAlwaysHits: true,
  criticalHit: Object.freeze({ threshold: 20, damageDiceMultiplier: 2 })
});

const requireRuntimeNumber = (value, path, { min = -Infinity, max = Infinity, integer = false } = {}) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number < min || number > max || (integer && !Number.isInteger(number))) {
    throw new Error(`${path} is not a supported ruleset number.`);
  }
  return number;
};

/**
 * Resolve the attack policy delivered with the exact BattleRoom binding. A
 * live room fails closed if its runtime envelope is absent or does not match
 * the persisted identity. Local/non-networked utility calls explicitly use
 * the core policy so existing isolated tools remain deterministic.
 * date_of_change: 2026-08-21
 */
const resolveRuntimeAttackRules = (battleState = {}) => {
  const runtime = battleState?.rulesetRuntime;
  const binding = battleState?.rulesetBinding;
  if (!runtime) {
    if (binding) throw new Error('The BattleRoom ruleset runtime is unavailable. Reconnect before resolving mechanics.');
    return CORE_ATTACK_RULES;
  }

  if (!binding) {
    throw new Error('The BattleRoom ruleset runtime has no immutable room binding. Reconnect before resolving mechanics.');
  }

  const runtimeBinding = runtime?.binding;
  const definition = runtime?.definition;
  const identityFields = ['source', 'packageId', 'versionId', 'definitionHash'];
  if (!runtimeBinding || (binding && identityFields.some((field) => String(binding[field] ?? '') !== String(runtimeBinding[field] ?? '')))) {
    throw new Error('The BattleRoom ruleset runtime does not match its immutable binding.');
  }
  if (!definition || (battleState?.ruleset && String(definition.id) !== String(battleState.ruleset))) {
    throw new Error('The BattleRoom ruleset runtime does not match its engine ruleset ID.');
  }

  const rules = definition?.baseRules?.attack;
  if (!rules || typeof rules.naturalOneAlwaysMisses !== 'boolean' || typeof rules.naturalTwentyAlwaysHits !== 'boolean') {
    throw new Error('The BattleRoom ruleset has no executable attack policy.');
  }
  return {
    attackRollModifier: requireRuntimeNumber(rules.attackRollModifier, 'ruleset.baseRules.attack.attackRollModifier'),
    damageRollModifier: requireRuntimeNumber(rules.damageRollModifier, 'ruleset.baseRules.attack.damageRollModifier'),
    naturalOneAlwaysMisses: rules.naturalOneAlwaysMisses,
    naturalTwentyAlwaysHits: rules.naturalTwentyAlwaysHits,
    criticalHit: {
      threshold: requireRuntimeNumber(rules.criticalHit?.threshold, 'ruleset.baseRules.attack.criticalHit.threshold', { min: 1, max: 20, integer: true }),
      damageDiceMultiplier: requireRuntimeNumber(rules.criticalHit?.damageDiceMultiplier, 'ruleset.baseRules.attack.criticalHit.damageDiceMultiplier', { min: 1, integer: true })
    }
  };
};

const hasUsableValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (isFiniteNumber(value)) return true;
  if (typeof value === 'string') return isNonEmptyString(value);
  return value != null;
};

const cloneResolvedValue = (value) => {
  if (Array.isArray(value)) return value.map((entry) => cloneResolvedValue(entry));
  if (value && typeof value === 'object') return { ...value };
  return value;
};

const normalizeConditionKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s_-]+/g, '');

const collectConditionKeys = (entity) => {
  const sources = [
    entity,
    entity?.entity,
    entity?.entityPayload,
    entity?.characterData,
    entity?.canonicalData,
    entity?.data,
    entity?.monsterData,
    entity?.monsterData?.data
  ];
  const keys = new Set();

  sources.forEach((source) => {
    if (!source || typeof source !== 'object') return;
    if (Array.isArray(source.conditions)) {
      source.conditions.forEach((condition) => {
        const key = normalizeConditionKey(condition);
        if (key) keys.add(key);
      });
    }
    if (source.statusEffects && typeof source.statusEffects === 'object') {
      Object.entries(source.statusEffects).forEach(([condition, active]) => {
        if (!active) return;
        const key = normalizeConditionKey(condition);
        if (key) keys.add(key);
      });
    }
  });

  return keys;
};

const hasCombatCondition = (entity, conditionNames) => {
  const keys = collectConditionKeys(entity);
  return conditionNames.some((condition) => keys.has(normalizeConditionKey(condition)));
};

const collectActiveEffectMetadata = (entity, effectName) => {
  const normalizedEffect = String(effectName || '').trim();
  const sources = [
    entity,
    entity?.entity,
    entity?.entityPayload,
    entity?.characterData,
    entity?.canonicalData,
    entity?.data
  ];

  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    if (source?.statusEffects?.[normalizedEffect] !== true) continue;
    const metadata = source?.statusEffectsMeta?.[normalizedEffect];
    if (metadata && typeof metadata === 'object') return metadata;
  }
  return null;
};

const addFlatModifierToDiceFormula = (formula, modifier) => {
  const amount = Number(modifier);
  const match = String(formula || '').trim().match(/^(\d+d\d+)(?:\s*([+-])\s*(\d+))?$/i);
  if (!match || !Number.isFinite(amount) || amount === 0) return formula;
  const existing = match[3] ? Number(match[3]) * (match[2] === '-' ? -1 : 1) : 0;
  const total = existing + amount;
  return `${match[1]}${total === 0 ? '' : `${total > 0 ? '+' : ''}${total}`}`;
};

const resolveStructuredMeleeStrengthDamageBonus = (actor, action) => {
  const effect = collectActiveEffectMetadata(actor, 'rage');
  const bonus = Number(effect?.meleeStrengthDamageBonus);
  if (!Number.isFinite(bonus) || bonus === 0) return 0;

  const ability = String(action?.ability || action?.attack?.ability || action?.weaponData?.ability || '')
    .trim()
    .toLowerCase();
  const attackType = String(action?.attack?.type || action?.attackType || '')
    .trim()
    .toLowerCase();
  const isMelee = attackType.includes('melee')
    || (action?.reach != null && action?.range == null && action?.attack?.range == null);

  return ability === 'strength' && isMelee ? bonus : 0;
};

const battleGridOptions = (battleState = {}) => ({
  gridSize: Number(battleState?.gridSize ?? battleState?.grid?.size),
  feetPerCell: Number(battleState?.feetPerCell ?? battleState?.grid?.feetPerCell ?? 5),
  gridType: battleState?.gridType ?? battleState?.grid?.type ?? 'square'
});

const distanceFeetBetween = (left, right, battleState = {}) => {
  const options = battleGridOptions(battleState);
  if (!Number.isFinite(options.gridSize) || options.gridSize <= 0 || !Number.isFinite(options.feetPerCell)) return Infinity;
  try {
    const leftCenter = RangeCalculator.getTokenCenter(left, options);
    const rightCenter = RangeCalculator.getTokenCenter(right, options);
    const distance = RangeCalculator.calculateGridDistance(leftCenter, rightCenter, options.gridType, options.feetPerCell);
    return Number.isFinite(Number(distance?.feet)) ? Number(distance.feet) : Infinity;
  } catch (_) {
    return Infinity;
  }
};

export const qualifyingCreatureNextToTarget = (attacker, target, battleState = {}, { requireAttackerAlly = false, distanceFeet = 5 } = {}) => {
  const tokens = Array.isArray(battleState?.tokens)
    ? battleState.tokens
    : (Array.isArray(battleState?.map?.tokens) ? battleState.map.tokens : []);
  return tokens.find((candidate) => candidate
    && !sameCombatEntity(candidate, attacker)
    && !sameCombatEntity(candidate, target)
    && !isCombatantIncapacitated(candidate)
    && (!requireAttackerAlly || areCombatAllies(attacker, candidate))
    && areCombatEnemies(candidate, target)
    && distanceFeetBetween(candidate, target, battleState) <= distanceFeet) ?? null;
};

const hasNamedFeature = hasNamedCombatFeature;

const sneakAttackUsedInSnapshot = (attacker, round, turn) => {
  const flags = attacker?.combatFlags;
  if (!flags || typeof flags !== 'object') return false;
  return Number(flags.sneakAttackUsedRound) === Number(round)
    && Number(flags.sneakAttackUsedTurn) === Number(turn);
};

const collectHiddenFrom = (entity) => {
  const sources = [
    entity?.combatFlags,
    entity?.entity?.combatFlags,
    entity?.entityPayload?.combatFlags,
    entity?.characterData?.combatFlags,
    entity?.canonicalData?.combatFlags,
    entity?.data?.combatFlags,
    entity?.monsterData?.combatFlags,
    entity?.monsterData?.data?.combatFlags
  ];

  return sources.flatMap((source) => (
    Array.isArray(source?.hiddenFrom) ? source.hiddenFrom : []
  ));
};

const resolvePassivePerception = (combatant) => {
  const candidateValues = [
    combatant?.passivePerception,
    combatant?.senses?.passivePerception,
    combatant?.monsterData?.data?.senses?.passivePerception,
    combatant?.characterData?.passivePerception
  ];

  for (const value of candidateValues) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) return numericValue;
  }

  const wisdomScore = Number(
    combatant?.abilities?.wisdom
    ?? combatant?.wisdom
    ?? combatant?.stats?.wis
  );
  return Number.isFinite(wisdomScore)
    ? 10 + Math.floor((wisdomScore - 10) / 2)
    : 10;
};

const matchesEntityIdentifier = (candidate, entity) => {
  const text = String(candidate || '').trim();
  if (!text) return false;
  if (['*', 'all', 'everyone'].includes(text.toLowerCase())) return true;

  return [
    entity?.id,
    entity?.combatantId,
    entity?.characterId,
    entity?.monsterId,
    entity?.name
  ].filter(Boolean).some((identifier) => String(identifier) === text);
};

// ---------------------------------------------------------------------------
// Sneak Attack helpers — shared by attack execution and the pre-roll preview.
// D&D 5e Rule (2014 PHB p.96; 2024 PHB Rogue): once per turn, extra damage when
// the attack uses a finesse or ranged weapon and the attacker has advantage, or
// another enemy of the target is within 5 feet and not incapacitated, provided
// the attacker does not have disadvantage.
// date_of_change: 2026-07-11 - Extracted from executeAttack inline closures so
// the Action Targeting popup can preview the same adjudication before rolling.
// ---------------------------------------------------------------------------
const sneakAttackFeaturePresent = (attacker) => {
  if (hasNamedFeature(attacker, 'Sneak Attack')) return true;
  // Also allow explicit rogue class marker
  const className = String(attacker?.class || attacker?.characterData?.class || attacker?.entityPayload?.class || '').toLowerCase();
  if (className.includes('rogue')) return true;
  const classes = attacker?.classes || attacker?.characterData?.classes || attacker?.canonicalData?.classes || attacker?.data?.classes || attacker?.entityPayload?.classes || [];
  if (Array.isArray(classes) && classes.some(c => String(c?.name || c?.className || '').toLowerCase().includes('rogue'))) return true;
  return false;
};

const sneakAttackDiceCount = (attacker) => {
  // 1) Structured feature with explicit dice
  const scanForDice = (collection) => {
    if (!Array.isArray(collection)) return 0;
    for (const f of collection) {
      const v = f?.dice || f?.damage || f?.damageDice || f?.extraDamage;
      if (typeof v === 'string') {
        const m = v.match(/(\d+)d6/i);
        if (m) return parseInt(m[1], 10) || 0;
      }
    }
    return 0;
  };
  const baskets = [
    attacker?.features,
    attacker?.entity?.features,
    attacker?.entityPayload?.features,
    attacker?.characterData?.features,
    attacker?.canonicalData?.features,
    attacker?.data?.features,
    attacker?.monsterData?.traits,
    attacker?.monsterData?.data?.traits,
    attacker?.monsterData?.specialAbilities,
    attacker?.monsterData?.data?.specialAbilities
  ];
  for (const b of baskets) {
    const arr = Array.isArray(b) ? b : (Array.isArray(b?.list) ? b.list : null);
    const n = scanForDice(arr);
    if (n > 0) return n;
  }

  // 2) Rogue class progression by level (no free-text parsing)
  const resolveRogueLevel = () => {
    const directSources = [
      attacker,
      attacker?.characterData,
      attacker?.canonicalData,
      attacker?.data,
      attacker?.entity,
      attacker?.entityPayload
    ];
    for (const source of directSources) {
      const classValue = source?.class;
      const className = String(classValue?.name || classValue?.className || classValue || '').toLowerCase();
      const level = Number(source?.level ?? classValue?.level);
      if (className.includes('rogue') && Number.isFinite(level) && level > 0) {
        return level;
      }
    }
    // Nested classes array
    const classArrays = [attacker?.classes, attacker?.characterData?.classes, attacker?.canonicalData?.classes, attacker?.data?.classes, attacker?.entityPayload?.classes];
    for (const ca of classArrays) {
      if (Array.isArray(ca)) {
        const rogue = ca.find(c => String(c?.name || c?.className || '').toLowerCase().includes('rogue'));
        const lvl = Number(rogue?.level);
        if (Number.isFinite(lvl) && lvl > 0) return lvl;
      }
    }
    // Compat originalData format
    const comp = attacker?.compat?.originalData?.classes;
    if (Array.isArray(comp)) {
      const rogue = comp.find(c => String(c?.name || c?.className || '').toLowerCase().includes('rogue'));
      const lvl = Number(rogue?.level);
      if (Number.isFinite(lvl) && lvl > 0) return lvl;
    }
    return 0;
  };
  const rLevel = resolveRogueLevel();
  if (rLevel > 0) {
    return Math.min(10, Math.max(1, Math.floor((rLevel + 1) / 2))); // 1d6 at 1st, +1d6 at 3rd,5th,...
  }
  return 0;
};

const collectActionResolutionSources = (action) => {
  const queue = [asRecord(action)];
  const visited = new Set();
  const sources = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || visited.has(current)) continue;
    visited.add(current);
    sources.push(current);

    [
      current.originalAction,
      current.weaponData,
      current.spellData,
      current.definition,
      current.actionData,
      current.action,
      current.attack
    ].forEach((candidate) => {
      const record = asRecord(candidate);
      if (record && !visited.has(record)) {
        queue.push(record);
      }
    });
  }

  return sources;
};

const selectResolvedActionValue = (sources, readers, predicate = hasUsableValue) => {
  for (const source of sources) {
    for (const reader of readers) {
      const value = reader(source);
      if (predicate(value)) {
        return value;
      }
    }
  }

  return undefined;
};

const parseDamageEntriesFromActionText = (text) => {
  if (!isNonEmptyString(text)) return [];

  const patterns = [
    /\((\d+d\d+(?:\s*[+-]\s*\d+)?)\)\s+([a-z]+)\s+damage/gi,
    /(\d+d\d+(?:\s*[+-]\s*\d+)?)\s+([a-z]+)\s+damage/gi
  ];
  const seen = new Set();
  const entries = [];

  patterns.forEach((pattern) => {
    for (const match of text.matchAll(pattern)) {
      const dice = String(match[1] || '').replace(/\s+/g, '');
      const type = String(match[2] || '').trim().toLowerCase();
      const key = `${dice}|${type}`;
      if (!dice || !type || seen.has(key)) continue;
      seen.add(key);
      entries.push({ dice, type });
    }
  });

  return entries;
};

const buildResolvedAttackPayload = (sources, action) => {
  const mergedAttack = {};

  for (let index = sources.length - 1; index >= 0; index -= 1) {
    const attackPayload = asRecord(sources[index]?.attack);
    if (attackPayload) {
      Object.assign(mergedAttack, cloneResolvedValue(attackPayload));
    }
  }

  const actionAttack = asRecord(action?.attack);
  if (actionAttack) {
    Object.assign(mergedAttack, cloneResolvedValue(actionAttack));
  }

  return Object.keys(mergedAttack).length > 0 ? mergedAttack : null;
};

const buildActionResolutionView = (action, context = {}) => {
  const baseAction = asRecord(action);
  if (!baseAction) return action;
  // Structured creature definitions are already executable. Prose/weapon-name
  // inference must not replace their base or trait-added dice at runtime.
  // SRD 5.1 structured mechanics; date_of_change: 2026-09-05
  if (baseAction.mechanics) return baseAction;

  const sources = collectActionResolutionSources(baseAction);
  const resolved = {
    ...baseAction
  };
  const resolvedAttack = buildResolvedAttackPayload(sources, baseAction);
  const resolvedText = selectResolvedActionValue(
    sources,
    [
      (source) => source.description,
      (source) => source.desc,
      (source) => source.text,
      (source) => source.notes,
      (source) => source.statLine
    ],
    isNonEmptyString
  );
  const parsedDamageEntries = parseDamageEntriesFromActionText(resolvedText || '');
  const resolvedDamageArray = selectResolvedActionValue(
    sources,
    [
      (source) => (Array.isArray(source.damage) && source.damage.length > 0 ? source.damage : undefined),
      (source) => (Array.isArray(source.attack?.damage) && source.attack.damage.length > 0 ? source.attack.damage : undefined)
    ],
    (value) => Array.isArray(value) && value.length > 0
  );
  const resolvedDamageFormula = selectResolvedActionValue(
    sources,
    [
      (source) => source.damageFormula,
      (source) => source.damageRoll,
      (source) => (typeof source.damage === 'string' ? source.damage : undefined),
      (source) => source.attack?.damageFormula,
      (source) => source.attack?.damageRoll,
      (source) => (typeof source.attack?.damage === 'string' ? source.attack.damage : undefined)
    ],
    isNonEmptyString
  );
  const resolvedDamageType = selectResolvedActionValue(
    sources,
    [
      (source) => source.damageType,
      (source) => source.attack?.damageType
    ],
    isNonEmptyString
  );
  const inferredWeaponRange = inferWeaponRange(baseAction);
  const resolvedRange = selectResolvedActionValue(
    sources,
    [
      (source) => source.range,
      (source) => source.attackRange,
      (source) => source.rangeFeet,
      (source) => source.attack?.range
    ],
    hasUsableValue
  ) ?? inferredWeaponRange;
  const inferredWeaponReach = inferWeaponReach(baseAction);
  const resolvedReach = selectResolvedActionValue(
    sources,
    [
      (source) => source.reach,
      (source) => source.attack?.reach
    ],
    hasUsableValue
  ) ?? inferredWeaponReach;
  const resolvedProperties = selectResolvedActionValue(
    sources,
    [
      (source) => source.properties,
      (source) => source.attack?.properties
    ],
    (value) => Array.isArray(value) && value.length > 0
  );
  const inferredWeaponAttackType = inferWeaponAttackType(baseAction);
  const resolvedAttackType = selectResolvedActionValue(
    sources,
    [
      (source) => source.attackType,
      (source) => source.kind,
      (source) => source.attack?.type
    ],
    isNonEmptyString
  ) ?? inferredWeaponAttackType;
  const resolvedAttackBonus = selectResolvedActionValue(
    sources,
    [
      (source) => source.attackBonus,
      (source) => source.toHit,
      (source) => source.hitBonus,
      (source) => source.bonus,
      (source) => source.attack?.toHit,
      (source) => source.attack?.bonus
    ],
    isFiniteNumber
  );
  const inferredWeaponDamageEntries = inferWeaponDamageEntries(baseAction, context);
  const mergedWeaponDamageEntries = mergeWeaponBaseDamageEntries(
    baseAction,
    Array.isArray(resolvedDamageArray) ? resolvedDamageArray : parsedDamageEntries,
    context
  );
  const inferredWeaponMagicBonus = inferWeaponMagicBonus(baseAction);

  if (!isNonEmptyString(resolved.description) && isNonEmptyString(resolvedText)) {
    resolved.description = resolvedText;
  }
  if (!hasUsableValue(resolved.range) && hasUsableValue(resolvedRange)) {
    resolved.range = cloneResolvedValue(resolvedRange);
  }
  if (!hasUsableValue(resolved.reach) && hasUsableValue(resolvedReach)) {
    resolved.reach = cloneResolvedValue(resolvedReach);
  }
  if (!Array.isArray(resolved.properties) || resolved.properties.length === 0) {
    if (Array.isArray(resolvedProperties) && resolvedProperties.length > 0) {
      resolved.properties = cloneResolvedValue(resolvedProperties);
    }
  }
  if (!isNonEmptyString(resolved.attackType) && isNonEmptyString(resolvedAttackType)) {
    resolved.attackType = resolvedAttackType;
  }
  if (!isFiniteNumber(resolved.attackBonus) && isFiniteNumber(resolvedAttackBonus)) {
    resolved.attackBonus = resolvedAttackBonus;
  }
  if (!isFiniteNumber(resolved.toHit) && isFiniteNumber(resolvedAttackBonus)) {
    resolved.toHit = resolvedAttackBonus;
  }
  if (!isFiniteNumber(resolved.bonus) && isFiniteNumber(resolvedAttackBonus)) {
    resolved.bonus = resolvedAttackBonus;
  }
  if (!isFiniteNumber(resolved.hitBonus) && isFiniteNumber(resolvedAttackBonus)) {
    resolved.hitBonus = resolvedAttackBonus;
  }

  if (mergedWeaponDamageEntries.length > (Array.isArray(resolved.damage) ? resolved.damage.length : 0)) {
    resolved.damage = cloneResolvedValue(mergedWeaponDamageEntries);
  } else if (!hasUsableValue(resolved.damage) && parsedDamageEntries.length > 0) {
    resolved.damage = parsedDamageEntries;
  } else if (!isNonEmptyString(resolved.damage) && isNonEmptyString(resolvedDamageFormula)) {
    resolved.damage = resolvedDamageFormula.trim();
  } else if (!hasUsableValue(resolved.damage) && inferredWeaponDamageEntries.length > 0) {
    resolved.damage = cloneResolvedValue(inferredWeaponDamageEntries);
  }
  if (!isNonEmptyString(resolved.damageFormula) && isNonEmptyString(resolvedDamageFormula)) {
    resolved.damageFormula = resolvedDamageFormula.trim();
  }
  if (!isNonEmptyString(resolved.damageType) && isNonEmptyString(resolvedDamageType)) {
    resolved.damageType = resolvedDamageType.trim();
  }
  if (!isFiniteNumber(resolved.magicBonus) && inferredWeaponMagicBonus) {
    resolved.magicBonus = inferredWeaponMagicBonus;
  }

  if (resolvedAttack) {
    if (!isNonEmptyString(resolvedAttack.type) && isNonEmptyString(resolvedAttackType)) {
      resolvedAttack.type = resolvedAttackType;
    }
    if (!hasUsableValue(resolvedAttack.range) && hasUsableValue(resolvedRange)) {
      resolvedAttack.range = cloneResolvedValue(resolvedRange);
    }
    if (!hasUsableValue(resolvedAttack.reach) && hasUsableValue(resolvedReach)) {
      resolvedAttack.reach = cloneResolvedValue(resolvedReach);
    }
    if ((!Array.isArray(resolvedAttack.properties) || resolvedAttack.properties.length === 0) && Array.isArray(resolvedProperties) && resolvedProperties.length > 0) {
      resolvedAttack.properties = cloneResolvedValue(resolvedProperties);
    }
    if (!isFiniteNumber(resolvedAttack.toHit) && isFiniteNumber(resolvedAttackBonus)) {
      resolvedAttack.toHit = resolvedAttackBonus;
    }
    if (!isFiniteNumber(resolvedAttack.bonus) && isFiniteNumber(resolvedAttackBonus)) {
      resolvedAttack.bonus = resolvedAttackBonus;
    }
    if (mergedWeaponDamageEntries.length > (Array.isArray(resolvedAttack.damage) ? resolvedAttack.damage.length : 0)) {
      resolvedAttack.damage = cloneResolvedValue(mergedWeaponDamageEntries);
    } else if ((!Array.isArray(resolvedAttack.damage) || resolvedAttack.damage.length === 0) && parsedDamageEntries.length > 0) {
      resolvedAttack.damage = parsedDamageEntries;
    } else if ((!Array.isArray(resolvedAttack.damage) || resolvedAttack.damage.length === 0) && inferredWeaponDamageEntries.length > 0) {
      resolvedAttack.damage = cloneResolvedValue(inferredWeaponDamageEntries);
    }
    if (!isNonEmptyString(resolvedAttack.damageFormula) && isNonEmptyString(resolvedDamageFormula)) {
      resolvedAttack.damageFormula = resolvedDamageFormula.trim();
    }
    if (!isNonEmptyString(resolvedAttack.damageType) && isNonEmptyString(resolvedDamageType)) {
      resolvedAttack.damageType = resolvedDamageType.trim();
    }
    if (!isFiniteNumber(resolvedAttack.magicBonus) && inferredWeaponMagicBonus) {
      resolvedAttack.magicBonus = inferredWeaponMagicBonus;
    }
    resolved.attack = resolvedAttack;
  }

  return resolved;
};

class CombatManager {
  // -----------------------
  // Core dice utilities
  // -----------------------

  static isPlayerControlled(entity) {
    const controller = String(
      entity?.controller
      || entity?.ownerRole
      || entity?.controlledBy
      || entity?.characterData?.controller
      || entity?.data?.controller
      || ''
    ).toLowerCase();
    const type = String(entity?.type || entity?.entity?.type || '').toLowerCase();
    return entity?.isPlayer === true
      || controller === 'player'
      || type === 'character'
      || Boolean(entity?.characterData || entity?.canonicalData?.characterClass || entity?.data?.characterClass);
  }

  static shouldUseManualRolls(entity) {
    const settings = getDiceAuto();
    return this.isPlayerControlled(entity)
      ? settings.playerAutomaticRolls === false
      : settings.gmAutomaticRolls === false;
  }

  static resolveAttackRules(battleState = {}) {
    return resolveRuntimeAttackRules(battleState);
  }

  static requestManualNumberSync({ title, prompt, min = 1, max = 20, fallback = min } = {}) {
    if (typeof window === 'undefined' || typeof window.prompt !== 'function') return null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      // eslint-disable-next-line no-alert
      const raw = window.prompt(`${title}\n${prompt}`, String(fallback));
      if (raw === null) return null;
      const value = Number.parseInt(String(raw).trim(), 10);
      if (Number.isInteger(value) && value >= min && value <= max) return value;
    }
    return null;
  }

  static requestManualD20Rolls(entity, { advantage = false, disadvantage = false, label = 'd20 roll' } = {}) {
    if (!this.shouldUseManualRolls(entity)) return null;
    const needsTwo = Boolean(advantage) !== Boolean(disadvantage);
    const count = needsTwo ? 2 : 1;
    const rolls = [];
    const mode = advantage && !disadvantage ? ' with advantage' : (disadvantage && !advantage ? ' with disadvantage' : '');
    for (let index = 0; index < count; index += 1) {
      const value = this.requestManualNumberSync({
        title: `Manual ${label}`,
        prompt: `Enter raw d20 face ${count > 1 ? `${index + 1} of ${count}` : ''}${mode}. VCS adds modifiers automatically.`,
        min: 1,
        max: 20,
        fallback: 10
      });
      if (value == null) return null;
      rolls.push(value);
    }
    return rolls;
  }

  static requestManualDiceRolls(entity, { diceCount, diceSize, formula, label = 'damage' } = {}) {
    if (!this.shouldUseManualRolls(entity) || diceCount <= 0 || diceSize <= 0) return null;
    const rolls = [];
    for (let index = 0; index < diceCount; index += 1) {
      const value = this.requestManualNumberSync({
        title: `Manual ${label}`,
        prompt: `Enter raw d${diceSize} face ${index + 1} of ${diceCount} for ${formula}. VCS adds any flat modifier automatically.`,
        min: 1,
        max: diceSize,
        fallback: Math.ceil(diceSize / 2)
      });
      if (value == null) return null;
      rolls.push(value);
    }
    return rolls;
  }

  /**
   * Roll dice in format XdY[+Z] with D&D 5e semantics
   * @param {string} formula - Dice formula (e.g., "2d6+3")
   * @param {boolean} log - Whether to log the roll details
   * @param {number} seed - Optional seed for deterministic results
   * @returns {object} - Result object with total, rolls array, formula details, and modifiers
   * 
   * D&D 5e Rule: Dice notation follows standard RPG conventions
   * date_of_change: 2025-08-31
   */
  static rollDice(formula, log = false, seed = null, options = {}) {
    try {
      const regex = /(\d+)d(\d+)(?:\s*([+-])\s*(\d+))?/;
      const match = formula?.match?.(regex);

      if (!match) {
        console.error(`[CombatManager] Invalid dice formula: ${formula}`);
        return { total: 0, rolls: [], formula };
      }

      const diceCount = parseInt(match[1], 10);
      const diceSize = parseInt(match[2], 10);
      const modifier = match[4] ? parseInt(match[4], 10) * (match[3] === '-' ? -1 : 1) : 0;

      // date_of_change: 2026-07-12 - Manual roll queue bridge
      // rule_reference: AGENTS.md (GM hooks; avoid native window.prompt UX; deterministic replay)
      // When the UI has already collected the needed manual values ahead of execution
      // (e.g. the Action Targeting panel's inline roll step), options.manualRollQueue is a
      // shared, mutable, ordered array of raw die faces. We consume exactly `diceCount` values
      // from the front, in the same order the engine would have requested them, so the
      // pre-collected roll and the roll actually used by the engine are identical. If the queue
      // doesn't have enough values queued for this call (e.g. a conditional roll the UI didn't
      // anticipate, like Bless/Bane or Sneak Attack), we fall back to the legacy prompt so play
      // is never silently blocked.
      const manualRolls = Array.isArray(options?.manualRolls)
        ? options.manualRolls
        : (Array.isArray(options?.manualRollQueue) && options.manualRollQueue.length >= diceCount
          ? options.manualRollQueue.splice(0, diceCount)
          : this.requestManualDiceRolls(options?.roller, {
            diceCount,
            diceSize,
            formula,
            label: options?.label || options?.purpose || 'damage'
          }));
      const rolls = [];
      for (let i = 0; i < diceCount; i++) {
        const manual = Number(manualRolls?.[i]);
        rolls.push(Number.isInteger(manual)
          ? Math.max(1, Math.min(diceSize, manual))
          : Math.floor(Math.random() * diceSize) + 1);
      }

      const diceTotal = rolls.reduce((sum, roll) => sum + roll, 0);
      const total = diceTotal + modifier;

      if (log) {
        debugLog(`[CombatManager] Roll ${formula}: [${rolls.join(', ')}] ${modifier ? `+ ${modifier}` : ''} = ${total}`);
      }

      return {
        total,
        rolls,
        diceTotal,
        modifier,
        formula
      };
    } catch (error) {
      console.error(`[CombatManager] Error rolling dice formula "${formula}":`, error);
      return { total: 0, rolls: [], formula };
    }
  }

  /**
   * Prompt the GM to select a damage type when any detail entry is missing/"untyped".
   * Returns { details, chosen } where details has untyped entries replaced by the chosen type.
   * If the GM cancels, returns null.
   * date_of_change: 2025-11-05
   * rule_reference: AGENTS.md §9 (no fallbacks; GM override hook)
   */
  static async ensureTypedDamage(details = [], context = {}) {
    try {
      if (!Array.isArray(details) || details.length === 0) return { details, chosen: null };
      const needsType = details.some(d => !d?.damageType || String(d.damageType).toLowerCase() === 'untyped');
      if (!needsType) return { details, chosen: null };

      const allowed = [
        'acid','cold','fire','force','lightning','necrotic','poison','psychic','radiant','thunder',
        'bludgeoning','piercing','slashing'
      ];
      const title = 'Select Damage Type';
      const prompt = `Missing damage type for ${context?.name || 'action'}${context?.target ? ' → ' + context.target : ''}.`;
      const choice = await requestChoice({ title, prompt, options: allowed });
      if (!choice) return null; // GM cancelled

      const chosen = String(choice);
      const patched = details.map(d => ({
        ...d,
        damageType: (!d?.damageType || String(d.damageType).toLowerCase() === 'untyped') ? chosen : d.damageType
      }));
      return { details: patched, chosen };
    } catch (e) {
      debugLog('[CombatManager.ensureTypedDamage] Failed to prompt for damage type:', e?.message || e);
      return null;
    }
  }

  /**
   * Roll with advantage or disadvantage (D&D 5e PHB p.173)
   * @param {string} formula - Base dice formula (e.g., "1d20")
   * @param {string} type - 'normal', 'advantage', 'disadvantage'
   * @param {boolean} log - Whether to log details
   * @returns {object} - Roll result with advantage/disadvantage details
   * 
   * D&D 5e Rule: Advantage = roll twice, take higher. Disadvantage = roll twice, take lower.
   * date_of_change: 2025-08-31
   */
  

  /** 
   * Roll a d20 with advantage/disadvantage handling per D&D 5e 2024 rules.
   * If both advantage and disadvantage are present, they cancel out (straight roll).
   * date_of_change: 2025-09-01
   */
  // @date_of_change: 2025-10-13 - Manual dice bridge
  // rule_reference: AGENTS.md (engine determinism; accept external/manual dice values via options)
  // If manualRolls are provided (array of 1-2 numbers), use them instead of RNG.
  static rollD20({ advantage = false, disadvantage = false, manualRolls = null } = {}) {
    // D&D 5e 2024 Rule: Advantage and disadvantage cancel each other out
    let rollType = 'normal';
    if (advantage && disadvantage) {
      rollType = 'normal'; // They cancel out
    } else if (advantage) {
      rollType = 'advantage';
    } else if (disadvantage) {
      rollType = 'disadvantage';
    }

    // If manual values were supplied, honor them and simulate rollWithAdvantage semantics
    if (Array.isArray(manualRolls) && manualRolls.length > 0) {
      const r1 = Number(manualRolls[0]) || 1;
      const r2 = Number(manualRolls[1] ?? manualRolls[0]) || 1;
      let chosen = r1;
      let discarded = r2;
      if (rollType === 'advantage') {
        chosen = Math.max(r1, r2);
        discarded = (chosen === r1 ? r2 : r1);
      } else if (rollType === 'disadvantage') {
        chosen = Math.min(r1, r2);
        discarded = (chosen === r1 ? r2 : r1);
      }
      const roll1 = { total: r1, rolls: [r1] };
      const roll2 = { total: r2, rolls: [r2] };
      const allRolls = rollType === 'normal' ? [roll1] : [roll1, roll2];
      return {
        value: chosen,
        total: chosen,
        rolls: rollType === 'normal' ? [r1] : [r1, r2],
        allRollDetails: allRolls,
        advantage: (rollType === 'advantage'),
        disadvantage: (rollType === 'disadvantage'),
        rollType,
        chosenValue: chosen,
        discardedValue: rollType === 'normal' ? undefined : discarded
      };
    }

    const result = this.rollWithAdvantage('1d20', rollType, true);
    
    return {
      value: result.total,
      rolls: result.allRolls ? [result.allRolls[0].total, result.allRolls[1].total] : [result.total],
      allRollDetails: result.allRolls || [{ total: result.total, rolls: result.rolls }],
      advantage: advantage,
      disadvantage: disadvantage,
      rollType: rollType,
      chosenValue: result.total,
      discardedValue: result.discardedRoll?.total
    };
  }

  /**
   * Roll with advantage or disadvantage (D&D 5e PHB p.173)
   * @param {string} formula - Base dice formula (e.g., "1d20")
   * @param {string} type - 'normal', 'advantage', 'disadvantage'
   * @param {boolean} log - Whether to log details
   * @returns {object} - Roll result with advantage/disadvantage details
   * 
   * D&D 5e Rule: Advantage = roll twice, take higher. Disadvantage = roll twice, take lower.
   * date_of_change: 2025-08-31
   */
  static rollWithAdvantage(formula, type = 'normal', log = false) {
    if (type === 'normal') {
      return this.rollDice(formula, log);
    }

    // Roll twice for advantage/disadvantage
    const roll1 = this.rollDice(formula, false);
    const roll2 = this.rollDice(formula, false);
    
    let chosenRoll, discardedRoll;
    if (type === 'advantage') {
      if (roll1.total >= roll2.total) {
        chosenRoll = roll1;
        discardedRoll = roll2;
      } else {
        chosenRoll = roll2;
        discardedRoll = roll1;
      }
    } else { // disadvantage
      if (roll1.total <= roll2.total) {
        chosenRoll = roll1;
        discardedRoll = roll2;
      } else {
        chosenRoll = roll2;
        discardedRoll = roll1;
      }
    }

    if (log) {
      debugLog(`[CombatManager] ${type}: [${roll1.rolls.join(',')}]=${roll1.total}, [${roll2.rolls.join(',')}]=${roll2.total} → chose ${chosenRoll.total}`);
    }

    return {
      ...chosenRoll,
      advantageType: type,
      allRolls: [roll1, roll2],
      discardedRoll
    };
  }

  /**
   * Compute an ability modifier from attacker ability scores.
   * @param {object} attacker
   * @param {string} abilityKey - 'strength'|'dexterity'|'constitution'|'intelligence'|'wisdom'|'charisma'
   * @returns {number} ability modifier
   * @date_of_change: 2025-10-14
   */
  static getAbilityModifier(attacker, abilityKey) {
    try {
      const abilities = attacker?.abilities ||
        attacker?.entity?.abilities ||
        attacker?.canonicalData?.abilities ||
        attacker?.monsterData?.abilities ||
        attacker?.characterData?.abilities ||
        attacker?.data?.abilities || {};

      const map = {
        strength: ['strength', 'str'],
        dexterity: ['dexterity', 'dex'],
        constitution: ['constitution', 'con'],
        intelligence: ['intelligence', 'int'],
        wisdom: ['wisdom', 'wis'],
        charisma: ['charisma', 'cha']
      };
      const keys = map[abilityKey] || [abilityKey];
      let score = 10;
      for (const k of keys) {
        if (typeof abilities?.[k] === 'number') { score = abilities[k]; break; }
      }
      return Math.floor((Number(score) - 10) / 2);
    } catch (_) {
      return 0;
    }
  }

  static getAbilityMod(score) {
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) return 0;
    return Math.floor((numericScore - 10) / 2);
  }

  static resolvePassivePerception(combatant) {
    return resolvePassivePerception(combatant);
  }

  static normalizeAbilityKey(value) {
    const normalized = String(value || '').trim().toLowerCase();
    const map = {
      str: 'strength',
      dex: 'dexterity',
      con: 'constitution',
      int: 'intelligence',
      wis: 'wisdom',
      cha: 'charisma'
    };
    const full = map[normalized] || normalized;
    return ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].includes(full) ? full : null;
  }

  static getProficiencyBonus(entity) {
    const candidates = [
      entity?.proficiencyBonus,
      entity?.data?.combatStats?.proficiencyBonus,
      entity?.data?.proficiencyBonus,
      entity?.characterData?.combatStats?.proficiencyBonus,
      entity?.characterData?.proficiencyBonus,
      entity?.canonicalData?.combatStats?.proficiencyBonus,
      entity?.canonicalData?.proficiencyBonus,
      entity?.monsterData?.proficiencyBonus,
      entity?.entity?.proficiencyBonus
    ];
    for (const value of candidates) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
    return 2;
  }

  static inferSpellcastingAbility(entity) {
    const labels = [];
    const collect = (value) => {
      if (!value) return;
      if (typeof value === 'string') labels.push(value);
      else if (Array.isArray(value)) value.forEach(collect);
      else if (typeof value === 'object') {
        labels.push(value.name, value.className, value.class, value.subclass, value.subclassName, value.archetype);
      }
    };
    [entity, entity?.characterData, entity?.canonicalData, entity?.data, entity?.entity].forEach((source) => {
      collect(source?.class);
      collect(source?.subclass);
      collect(source?.classes);
      collect(source?.archetype);
    });
    const text = labels.filter(Boolean).join(' ').toLowerCase();
    if (/(arcane trickster|eldritch knight|wizard|artificer)/.test(text)) return 'intelligence';
    if (/(cleric|druid|ranger)/.test(text)) return 'wisdom';
    if (/(bard|paladin|sorcerer|warlock)/.test(text)) return 'charisma';
    return null;
  }

  static getSpellcastingStats(entity) {
    const sources = [
      entity?.spellcasting,
      entity?.spells,
      entity?.characterData?.spellcasting,
      entity?.characterData?.spells,
      entity?.canonicalData?.spellcasting,
      entity?.canonicalData?.spells,
      entity?.data?.spellcasting,
      entity?.data?.spells,
      entity?.entity?.spellcasting,
      entity?.entity?.spells
    ].filter(Boolean);
    const numeric = (...values) => {
      for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return null;
    };
    let ability = null;
    let attackBonus = null;
    let saveDC = null;
    for (const source of sources) {
      ability = ability || this.normalizeAbilityKey(source.ability ?? source.spellcastingAbility ?? source.spellAbility);
      attackBonus = attackBonus ?? numeric(source.attackBonus, source.spellAttackBonus, source.toHit);
      saveDC = saveDC ?? numeric(source.saveDC, source.saveDc, source.spellSaveDC);
    }
    ability = ability || this.inferSpellcastingAbility(entity);
    if (ability) {
      const abilityMod = this.getAbilityModifier(entity, ability);
      const proficiency = this.getProficiencyBonus(entity);
      attackBonus = attackBonus ?? abilityMod + proficiency;
      saveDC = saveDC ?? 8 + abilityMod + proficiency;
    }
    return { ability, attackBonus, saveDC };
  }

  /**
   * Determine attack bonus from action/attacker data.
   * Priority: explicit action bonuses → computed (ability + proficiency)
   * @param {object} attacker
   * @param {object} action
   * @returns {number}
   * @date_of_change: 2025-10-14
   */
  static getAttackBonus(attacker, action) {
    const resolvedAction = buildActionResolutionView(action);

    // 1) Use explicit to-hit bonuses when present
    const directCandidates = [
      resolvedAction?.attackBonus,
      resolvedAction?.toHit,
      resolvedAction?.hitBonus,
      resolvedAction?.bonus,
      resolvedAction?.attack?.toHit,
      resolvedAction?.attack?.bonus
    ];
    for (const c of directCandidates) {
      if (typeof c === 'number' && Number.isFinite(c)) return c;
    }

    const actionTypeText = [
      resolvedAction?.type,
      resolvedAction?.actionType,
      resolvedAction?.attackType,
      resolvedAction?.attack?.type,
      resolvedAction?.category
    ].filter(Boolean).join(' ').toLowerCase();
    const isSpellAttack = actionTypeText.includes('spell')
      || resolvedAction?.spellLevel != null
      || (resolvedAction?.level != null && resolvedAction?.type === 'spell');
    if (isSpellAttack) {
      const spellcasting = this.getSpellcastingStats(attacker);
      if (Number.isFinite(spellcasting.attackBonus)) return spellcasting.attackBonus;
    }

    // 2) Compute from ability + proficiency
    // Determine attack type and preferred ability
    const actionTypeStr = (resolvedAction?.attackType || resolvedAction?.attack?.type || '').toString().toLowerCase();
    const isRanged = actionTypeStr.includes('ranged');

    // Allow explicit ability on action.attack.ability
    let abilityKey = null;
    if (typeof resolvedAction?.attack?.ability === 'string') {
      const a = resolvedAction.attack.ability.toLowerCase();
      if (['strength','dexterity','constitution','intelligence','wisdom','charisma','str','dex','con','int','wis','cha'].includes(a)) {
        abilityKey = this.normalizeAbilityKey(a);
      }
    }
    if (!abilityKey) abilityKey = isRanged ? 'dexterity' : 'strength';

    // Finesse property: choose best of STR/DEX
    const hasFinesse = (
      (Array.isArray(resolvedAction?.properties) && resolvedAction.properties.some(p => typeof p === 'string' && p.toLowerCase() === 'finesse')) ||
      (Array.isArray(resolvedAction?.attack?.properties) && resolvedAction.attack.properties.some(p => typeof p === 'string' && p.toLowerCase() === 'finesse'))
    );
    let abilityMod = this.getAbilityModifier(attacker, abilityKey);
    if (hasFinesse) {
      const strMod = this.getAbilityModifier(attacker, 'strength');
      const dexMod = this.getAbilityModifier(attacker, 'dexterity');
      abilityMod = Math.max(strMod, dexMod);
    }

    // Proficiency bonus
    const pb = this.getProficiencyBonus(attacker);

    const proficient = (resolvedAction?.proficient !== false); // default true
    const miscExtras = [
      resolvedAction?.attack?.extraBonus,
      resolvedAction?.extraBonus
    ].filter(v => typeof v === 'number' && Number.isFinite(v));
    const magicBonuses = [
      resolvedAction?.attack?.magicalBonus,
      resolvedAction?.attack?.magicBonus,
      resolvedAction?.attack?.enhancementBonus,
      resolvedAction?.magicalBonus,
      resolvedAction?.magicBonus,
      resolvedAction?.enhancementBonus
    ].filter(v => typeof v === 'number' && Number.isFinite(v));
    const extra = miscExtras.reduce((a, b) => a + b, 0)
      + (magicBonuses.length ? Math.max(...magicBonuses) : inferWeaponMagicBonus(resolvedAction));
    return abilityMod + (proficient ? pb : 0) + extra;
  }

  /**
   * Calculate attack roll against target AC with D&D 5e 2024 mechanics
   * @param {object} attacker - The attacking entity
   * @param {object} target - The target entity  
   * @param {object} action - The attack action
   * @param {object} options - Attack options (advantage, disadvantage, etc.)
   * @returns {object} - Attack roll result with hit determination
   * 
   * D&D 5e 2024 Rule: Attack roll = 1d20 + ability modifier + proficiency bonus (if proficient)
   * Critical hit on natural 20, critical miss on natural 1
   * Advantage and disadvantage cancel each other out
   * date_of_change: 2025-09-01
   */
  static calculateAttackRoll(attacker, target, action, options = {}) {
    try {
      const attackRules = options?.attackRules || CORE_ATTACK_RULES;
      const baseAttackBonus = CombatManager.getAttackBonus(attacker, action);
      const attackBonus = baseAttackBonus + requireRuntimeNumber(
        attackRules.attackRollModifier,
        'ruleset.baseRules.attack.attackRollModifier'
      );

      const manualRolls = options?.dice?.manualRolls
        || options?.manualRolls
        || this.requestManualD20Rolls(attacker, {
          advantage: !!options.advantage,
          disadvantage: !!options.disadvantage,
          label: `${action?.name || 'attack'} attack roll`
        });
      const d20 = CombatManager.rollD20({
        advantage: !!options.advantage,
        disadvantage: !!options.disadvantage,
        // @date_of_change: 2025-10-13 - Manual dice bridge
        manualRolls: manualRolls || null
      });

      let totalAttackRoll = d20.value + attackBonus;

      // date_of_change: 2025-10-25 - Effects: Bless/Bane dice on attack rolls
      // Bless: add 1d4 to attack rolls; Bane: subtract 1d4 from attack rolls (RAW 5e)
      const extraDice = [];
      try {
        const se = attacker?.statusEffects || {};
        if (se.bless) {
          const blessRoll = this.rollDice('1d4', false, null, { roller: attacker, label: 'Bless attack modifier', manualRollQueue: options?.manualRollQueue });
          totalAttackRoll += blessRoll.total;
          extraDice.push({ source: 'Bless', formula: '1d4', total: blessRoll.total, rolls: blessRoll.rolls });
        }
        if (se.bane) {
          const baneRoll = this.rollDice('1d4', false, null, { roller: attacker, label: 'Bane attack modifier', manualRollQueue: options?.manualRollQueue });
          totalAttackRoll -= baneRoll.total;
          extraDice.push({ source: 'Bane', formula: '-1d4', total: -baneRoll.total, rolls: baneRoll.rolls });
        }
      } catch (_) {}
      // @date_of_change: 2025-10-14 - Robust AC retrieval across canonical and legacy paths
      const resolveAC = (t) => {
        try {
          if (typeof t?.ac === 'number') return t.ac;
          if (typeof t?.armorClass === 'number') return t.armorClass;
          if (t?.ac && typeof t.ac?.value === 'number') return t.ac.value;
          const e = t?.entity || {};
          if (typeof e?.ac === 'number') return e.ac;
          if (e?.ac && typeof e.ac?.value === 'number') return e.ac.value;
          const c = t?.canonicalData || {};
          if (typeof c?.ac === 'number') return c.ac;
          if (c?.ac && typeof c.ac?.value === 'number') return c.ac.value;
          const m = t?.monsterData?.data || t?.monsterData || {};
          if (typeof m?.ac === 'number') return m.ac;
          if (m?.ac && typeof m.ac?.value === 'number') return m.ac.value;
          const d = t?.data || {};
          if (typeof d?.ac === 'number') return d.ac;
          if (d?.ac && typeof d.ac?.value === 'number') return d.ac.value;
        } catch (_) {}
        return 10;
      };
      const targetAC = resolveAC(target);

      const criticalThreshold = requireRuntimeNumber(
        attackRules.criticalHit?.threshold,
        'ruleset.baseRules.attack.criticalHit.threshold',
        { min: 1, max: 20, integer: true }
      );
      const isCriticalFail = attackRules.naturalOneAlwaysMisses === true && d20.value === 1;
      const automaticHit = attackRules.naturalTwentyAlwaysHits === true && d20.value === 20;
      const isHit = !isCriticalFail && (automaticHit || totalAttackRoll >= targetAC);
      // Critical-range policy changes damage on a successful hit; it cannot
      // turn an otherwise missed attack into a contradictory critical miss.
      // date_of_change: 2026-08-21
      const isCritical = isHit && d20.value >= criticalThreshold;

      return {
        roll: d20.value,
        rolls: d20.rolls, // Array of all d20 values rolled (1 for normal, 2 for adv/disadv)
        allRollDetails: d20.allRollDetails, // Detailed roll information
        total: totalAttackRoll,
        attackBonus,
        baseAttackBonus,
        targetAC,
        isHit,
        isCritical,
        isCriticalFail,
        automaticHit,
        rulesetAttackModifier: attackBonus - baseAttackBonus,
        // Report effective state after cancellation; downstream rules such as Sneak
        // Attack must treat simultaneous advantage/disadvantage as neither.
        advantage: d20.rollType === 'advantage',
        disadvantage: d20.rollType === 'disadvantage',
        rollType: d20.rollType, // 'normal', 'advantage', 'disadvantage'
        chosenValue: d20.chosenValue,
        discardedValue: d20.discardedValue,
        extraDice // [{source, formula, total, rolls}] for audit/logging
      };
    } catch (error) {
      console.error('[CombatManager] Error calculating attack roll:', error);
      return {
        roll: 0,
        rolls: [],
        total: 0,
        attackBonus: 0,
        targetAC: 10,
        isHit: false,
        isCritical: false,
        isCriticalFail: false,
        error: true
      };
    }
  }

  /**
   * Calculate damage for a successful attack with D&D 5e 2024 critical hit rules
   * @param {object} action - The attack action being used
   * @param {boolean} isCritical - Whether the attack was a critical hit
   * @returns {object} - Result object with damage information
   * 
   * D&D 5e 2024 Rule: Critical hits double the dice rolled, not the modifier
   * PHB 2024: "When you score a critical hit, you get to roll extra dice for the attack's damage"
   * Only weapon/spell damage dice are doubled, not flat bonuses or additional damage
   * date_of_change: 2025-09-01
   */
  /**
   * Build the normalized damage-component list [{ formula, type }] for an action.
   * Extracted from calculateDamage so both the real roll path and the pre-roll
   * prediction path (getDamageRollPlan) share one source of truth for how many
   * dice/what size each component needs — no drift between preview and execution.
   * date_of_change: 2026-07-12
   */
  static buildDamageComponents(resolvedAction) {
    // @date_of_change: 2025-10-27 - Multi-component damage handling
    // rule_reference: PHB 2024 Damage Types and Resistance/Immunity — apply modifiers per damage type
    // Accept inputs in any of the following shapes (no silent guessing):
    // - action.damage: Array<{ dice|formula, bonus?, type }>
    // - action.damageFormula: string and action.damageType: string
    // - action.attack.damage: Array<{ dice|formula, bonus?, type }>
    const components = [];
    const pushComponent = (formulaStr, typeStr, bonus) => {
      let f = String(formulaStr || '').trim();
      const b = Number(bonus);
      if (Number.isFinite(b) && b !== 0 && !/\+\s*\d+/.test(f)) {
        f = `${f}+${b}`;
      }
      components.push({ formula: f || '1d4', type: String(typeStr || 'untyped') });
    };

    if (Array.isArray(resolvedAction?.damage)) {
      resolvedAction.damage.forEach(d => pushComponent(d?.dice || d?.formula || d?.damage || d?.damageFormula, d?.type || d?.damageType || resolvedAction?.damageType, d?.bonus));
    } else if (typeof resolvedAction?.damageFormula === 'string' && resolvedAction.damageFormula) {
      pushComponent(resolvedAction.damageFormula, resolvedAction?.damageType);
    } else if (typeof resolvedAction?.damage === 'string' && resolvedAction.damage) {
      pushComponent(resolvedAction.damage, resolvedAction?.damageType);
    } else if (Array.isArray(resolvedAction?.attack?.damage)) {
      resolvedAction.attack.damage.forEach(d => pushComponent(d?.dice || d?.formula || d?.damage || d?.damageFormula, d?.type || d?.damageType || resolvedAction?.attack?.damageType || resolvedAction?.damageType, d?.bonus));
    } else if (typeof resolvedAction?.attack?.damageFormula === 'string' && resolvedAction.attack.damageFormula) {
      pushComponent(resolvedAction.attack.damageFormula, resolvedAction?.attack?.damageType || resolvedAction?.damageType);
    } else if (typeof resolvedAction?.attack?.damage === 'string' && resolvedAction.attack.damage) {
      pushComponent(resolvedAction.attack.damage, resolvedAction?.attack?.damageType || resolvedAction?.damageType);
    }

    return components;
  }

  /**
   * Predict the ordered sequence of manual dice entries calculateDamage will need for an
   * action, WITHOUT rolling anything. Used by the UI to know exactly how many "enter dN face"
   * fields to show inline (and in what order) before it calls the real execution path with a
   * matching manualRollQueue, so the values the player enters are the values actually used.
   * Mirrors calculateDamage's own rollDice call order exactly: per component, base roll, then
   * (if critical) a same-size crit roll — matching the "double dice, not modifiers" PHB rule.
   * date_of_change: 2026-07-12
   */
  static getDamageRollPlan(action, isCritical = false, options = {}) {
    const damageActor = options?.attacker || options?.roller || null;
    const resolvedAction = buildActionResolutionView(action, {
      abilityModifierForAbility: (ability) => (
        damageActor ? CombatManager.getAbilityModifier(damageActor, ability) : 0
      )
    });
    const components = CombatManager.buildDamageComponents(resolvedAction);
    const criticalDiceMultiplier = isCritical
      ? requireRuntimeNumber(options?.criticalDiceMultiplier ?? 2, 'ruleset.baseRules.attack.criticalHit.damageDiceMultiplier', { min: 1, integer: true })
      : 1;
    const plan = [];
    components.forEach((comp) => {
      const diceMatch = comp.formula.match(/(\d+)d(\d+)/);
      if (!diceMatch) return;
      const diceCount = parseInt(diceMatch[1], 10);
      const diceSize = parseInt(diceMatch[2], 10);
      plan.push({ diceCount, diceSize, type: comp.type, phase: 'base' });
      for (let criticalIndex = 1; criticalIndex < criticalDiceMultiplier; criticalIndex += 1) {
        plan.push({ diceCount, diceSize, type: comp.type, phase: 'critical', criticalIndex });
      }
    });
    return plan;
  }

  static calculateDamage(action, isCritical = false, options = {}) {
    try {
      const damageActor = options?.attacker || options?.roller || null;
      const resolvedAction = buildActionResolutionView(action, {
        abilityModifierForAbility: (ability) => (
          damageActor ? CombatManager.getAbilityModifier(damageActor, ability) : 0
        )
      });

      let components = CombatManager.buildDamageComponents(resolvedAction);
      const damageRollModifier = requireRuntimeNumber(
        options?.damageRollModifier ?? 0,
        'ruleset.baseRules.attack.damageRollModifier'
      );
      const criticalDiceMultiplier = isCritical
        ? requireRuntimeNumber(options?.criticalDiceMultiplier ?? 2, 'ruleset.baseRules.attack.criticalHit.damageDiceMultiplier', { min: 1, integer: true })
        : 1;
      if (damageRollModifier !== 0) {
        components = components.map((component) => ({
          ...component,
          formula: addFlatModifierToDiceFormula(component.formula, damageRollModifier),
          rulesetDamageModifier: damageRollModifier
        }));
      }
      const structuredMeleeStrengthBonus = resolveStructuredMeleeStrengthDamageBonus(damageActor, resolvedAction);
      if (structuredMeleeStrengthBonus && components.length > 0) {
        components = components.map((component, index) => (
          index === 0
            ? {
                ...component,
                formula: addFlatModifierToDiceFormula(component.formula, structuredMeleeStrengthBonus),
                activeEffectBonus: structuredMeleeStrengthBonus,
                activeEffect: 'Rage'
              }
            : component
        ));
      }

      if (components.length === 0) {
        // No fallbacks: raise an explicit error to surface missing damage data
        throw new Error('calculateDamage: No valid damage components found on action');
      }

      let grandTotal = 0;
      const details = [];
      for (const comp of components) {
        const baseRoll = this.rollDice(comp.formula, true, null, {
          roller: options?.roller,
          label: options?.label || `${resolvedAction?.name || 'damage'} damage`,
          manualRollQueue: options?.manualRollQueue
        });
        let totalDamage = baseRoll.total;
        let allRolls = [...baseRoll.rolls];
        let criticalRolls = [];

        if (isCritical) {
          // Multiply only the dice portion; modifiers apply once. The exact
          // multiplier is supplied by the room's immutable attack policy.
          // date_of_change: 2026-08-21
          const diceMatch = comp.formula.match(/(\d+d\d+)/);
          if (diceMatch) {
            const diceOnly = diceMatch[1];
            for (let criticalIndex = 1; criticalIndex < criticalDiceMultiplier; criticalIndex += 1) {
              const critRoll = this.rollDice(diceOnly, true, null, {
                roller: options?.roller,
                label: options?.label || `${resolvedAction?.name || 'critical damage'} critical damage`,
                manualRollQueue: options?.manualRollQueue
              });
              criticalRolls.push(...critRoll.rolls);
              totalDamage += critRoll.total;
            }
            allRolls = [...allRolls, ...criticalRolls];
          }
        }

        grandTotal += totalDamage;
        details.push({
          formula: comp.formula,
          damageType: String(comp.type || 'untyped'),
          rolls: allRolls,
          baseRolls: baseRoll.rolls,
          criticalRolls,
          total: totalDamage,
          ...(comp.activeEffectBonus ? {
            activeEffectBonus: comp.activeEffectBonus,
            activeEffect: comp.activeEffect
          } : {}),
          ...(comp.rulesetDamageModifier ? { rulesetDamageModifier: comp.rulesetDamageModifier } : {}),
          isCritical
        });
      }

      const combinedFormula = components.map(c => `${c.formula} ${c.type}`).join(' + ');
      return {
        total: grandTotal,
        formula: combinedFormula,
        rolls: details.flatMap(d => d.rolls || []),
        baseRolls: details.flatMap(d => d.baseRolls || []),
        criticalRolls: details.flatMap(d => d.criticalRolls || []),
        baseDiceTotal: undefined,
        modifier: undefined,
        details,
        isCritical,
        criticalDiceMultiplier,
        rulesetDamageModifier: damageRollModifier
      };
    } catch (error) {
      console.error('[CombatManager] Error calculating damage:', error);
      return {
        total: 0,
        details: [],
        error: true
      };
    }
  }

  /**
   * Apply damage mitigation (immunity/resistance/vulnerability) per damage component.
   * @param {object} target - Target entity with canonical mitigation fields
   * @param {Array} details - Array of { damageType, total, ... }
   * @returns {{ total: number, details: Array, summary: string }}
   * @date_of_change: 2025-10-27
   * rule_reference: PHB 2024 Damage Resistance/Immunity/Vulnerability — apply per type after other modifiers
   */
  static applyMitigationToDetails(target, details = []) {
    const { immunities, resistances, vulnerabilities } = CombatManager.getTargetMitigationSets(target);

    const mitigated = [];
    let total = 0;
    let originalTotal = 0;
    const summaryFlags = new Set();

    for (const d of details) {
      const typeKey = String(d?.damageType || 'untyped').toLowerCase();
      const originalComponentTotal = Number(d?.total) || 0;
      let mTotal = originalComponentTotal;
      let flag = '';
      if (typeKey && immunities.includes(typeKey)) {
        mTotal = 0; flag = 'IMMUNE';
      } else if (typeKey && resistances.includes(typeKey)) {
        mTotal = Math.floor(mTotal / 2); flag = 'RESISTANT';
      } else if (typeKey && vulnerabilities.includes(typeKey)) {
        mTotal = mTotal * 2; flag = 'VULNERABLE';
      }
      if (flag) summaryFlags.add(flag);
      total += mTotal;
      originalTotal += originalComponentTotal;
      mitigated.push({
        ...d,
        originalTotal: originalComponentTotal,
        total: mTotal,
        mitigatedTotal: mTotal,
        mitigation: flag
      });
    }

    let summary = '';
    if (summaryFlags.size === 1) summary = ` (${[...summaryFlags][0]})`;
    else if (summaryFlags.size > 1) summary = ' (MIXED)';
    return { total, originalTotal, details: mitigated, summary };
  }

  /**
   * Retrieve canonical mitigation sets from target.
   * Prefers top-level canonical fields; avoids deep legacy fallbacks per no-fallbacks policy.
   * @date_of_change: 2025-10-27
   */
  static getTargetMitigationSets(target) {
    const toList = (v) => Array.isArray(v) ? v.map(x => String(x).toLowerCase()) : [];
    // Prefer canonical top-level; if not present, allow one-hop characterData/monsterData/canonicalData/data
    const pick = (obj) => obj?.resistanceBlock ? ({
      immunities: toList(obj.resistanceBlock.immune),
      resistances: toList(obj.resistanceBlock.resist),
      vulnerabilities: toList(obj.resistanceBlock.vuln)
    }) : ({
      immunities: toList(obj?.damageImmunities),
      resistances: toList(obj?.damageResistances),
      vulnerabilities: toList(obj?.damageVulnerabilities)
    });

    let sets = pick(target);
    let resolvedSets = sets;
    const paths = [target?.characterData, target?.monsterData, target?.canonicalData, target?.data];
    if (!target?.resistanceBlock && !sets.immunities.length && !sets.resistances.length && !sets.vulnerabilities.length) {
      for (const p of paths) {
        sets = pick(p);
        if (sets.immunities.length || sets.resistances.length || sets.vulnerabilities.length) {
          resolvedSets = sets;
          break;
        }
      }
    }

    // Executable effects contribute mitigation through explicit metadata. The
    // effect executor records the rule; this generic damage path consumes it.
    // date_of_change: 2026-08-20
    const activeRage = collectActiveEffectMetadata(target, 'rage');
    const dynamicResistances = toList(activeRage?.damageResistances);
    return {
      immunities: [...new Set(resolvedSets.immunities)],
      resistances: [...new Set([...resolvedSets.resistances, ...dynamicResistances])],
      vulnerabilities: [...new Set(resolvedSets.vulnerabilities)]
    };
  }

  /**
   * Calculate healing for a healing spell
   * @param {object} spell - The healing spell object with healing formula
   * @param {boolean} isCritical - Whether this is a critical heal (unused for spells)
   * @returns {object} - Healing calculation result with total, formula, and rolls
   *
   * D&D 5e Rule: Healing spells restore hit points but cannot exceed max HP
   * @date_of_change: 2025-09-19 - Added healing calculation for spells like Cure Wounds, Healing Word
   */
  static calculateHealing(spell, isCritical = false) {
    try {
      const resolvedSpell = buildActionResolutionView(spell);

      // Resolve a healing specification from multiple supported shapes:
      // - spell.healing: string formula ("1d8+3"), number amount (10), or array [{ dice|formula|amount, type }]
      // - spell.damage with entry type: 'healing' (string or array form)
      let healingSpec = resolvedSpell?.healing ?? null;

      // If no explicit 'healing', try to derive from damage entries with type 'healing'
      if (healingSpec == null && resolvedSpell && Array.isArray(resolvedSpell.damage)) {
        const healEntry = resolvedSpell.damage.find(d => String(d?.type || d?.damageType || '').toLowerCase().includes('healing')) || null;
        if (healEntry) {
          healingSpec = healEntry.dice || healEntry.formula || healEntry.amount || healEntry.value || null;
        }
      } else if (healingSpec == null && typeof resolvedSpell?.damage === 'string' && String(resolvedSpell?.damageType || '').toLowerCase() === 'healing') {
        healingSpec = resolvedSpell.damage;
      }

      // If still nothing, treat as zero healing (do not assume defaults)
      if (healingSpec == null) {
        return { total: 0, details: [] };
      }

      // Handle numeric flat healing
      if (typeof healingSpec === 'number' && Number.isFinite(healingSpec)) {
        const n = Number(healingSpec) || 0;
        return {
          total: n,
          formula: String(n),
          rolls: [],
          baseDiceTotal: 0,
          modifier: n,
          details: [{ formula: String(n), healingType: 'magical', rolls: [], total: n }]
        };
      }

      // Handle array form [{ dice|formula|amount }]
      if (Array.isArray(healingSpec) && healingSpec.length > 0) {
        const first = healingSpec[0] || {};
        if (typeof first.amount === 'number') {
          const n = Number(first.amount) || 0;
          return {
            total: n,
            formula: String(n),
            rolls: [],
            baseDiceTotal: 0,
            modifier: n,
            details: [{ formula: String(n), healingType: String(first.healingType || 'magical'), rolls: [], total: n }]
          };
        }
        const asFormula = first.dice || first.formula || null;
        if (asFormula) {
          const baseRoll = this.rollDice(asFormula, true);
          return {
            total: baseRoll.total,
            formula: asFormula,
            rolls: baseRoll.rolls,
            baseDiceTotal: baseRoll.diceTotal,
            modifier: baseRoll.modifier,
            details: [{ formula: asFormula, healingType: String(first.healingType || 'magical'), rolls: baseRoll.rolls, total: baseRoll.total }]
          };
        }
      }

      // Handle string formula ("1d8+3")
      if (typeof healingSpec === 'string') {
        const healingFormula = healingSpec;
        const baseRoll = this.rollDice(healingFormula, true);
        return {
          total: baseRoll.total,
          formula: healingFormula,
          rolls: baseRoll.rolls,
          baseDiceTotal: baseRoll.diceTotal,
          modifier: baseRoll.modifier,
          details: [{ formula: healingFormula, healingType: 'magical', rolls: baseRoll.rolls, total: baseRoll.total }]
        };
      }

      // Fallback: unsupported shape
      return { total: 0, details: [] };
    } catch (error) {
      console.error('[CombatManager] Error calculating healing:', error);
      return {
        total: 0,
        details: [],
        error: true
      };
    }
  }

  /**
   * Apply damage to a target entity with D&D 5e rules
   * @param {object} target - The target entity
   * @param {number} damage - The amount of damage to apply
   * @returns {object} - Updated target entity
   * 
   * D&D 5e Rule: Temporary HP is lost first; damage reduces current HP and cannot go below 0
   * @date_of_change: 2025-10-14 - Robust HP detection across token/entity/character/monster paths
   */
  static applyDamage(target, damage) {
    try {
      // Determine best HP source object among common holders
      const sources = [
        { key: 'self', obj: target },
        { key: 'entity', obj: target?.entity },
        { key: 'canonicalData', obj: target?.canonicalData },
        { key: 'monsterData', obj: target?.monsterData },
        { key: 'monsterData.data', obj: target?.monsterData?.data },
        { key: 'characterData', obj: target?.characterData },
        { key: 'data', obj: target?.data }
      ];
      const hasHp = (o) => !!(o && (
        typeof o.hp === 'number' ||
        o.hpObj ||
        typeof o.maxHp === 'number' ||
        (o.data && (typeof o.data.hp === 'number' || o.data.hpObj || typeof o.data.maxHp === 'number'))
      ));
      const chosen = sources.find(s => hasHp(s.obj)) || sources[0];

      // Unify HP view from chosen source
      const unified = createUnifiedHP(chosen.obj || {});

      // Gather temporary HP from common locations
      const tempCandidates = [
        target?.tempHp,
        target?.data?.tempHp,
        target?.characterData?.tempHp,
        target?.monsterData?.tempHp,
        target?.entity?.tempHp
      ];
      let tempHp = 0;
      for (const t of tempCandidates) {
        if (Number.isFinite(t)) { tempHp = Number(t); break; }
      }

      let remaining = Math.max(0, Number(damage) || 0);
      // Temporary HP is lost first (PHB)
      if (tempHp > 0 && remaining > 0) {
        const tempLoss = Math.min(tempHp, remaining);
        tempHp -= tempLoss;
        remaining -= tempLoss;
      }

      const damageToHP = Math.max(0, remaining);
      const newCurrent = Math.max(0, (Number(unified.current) || 0) - damageToHP);
      const newMax = Number(unified.max) || Math.max(newCurrent, 1);

      // Helper to apply unified HP back into a given holder while preserving structure
      const applyTo = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        const updated = updateEntityHP(obj, { current: newCurrent, max: newMax });
        if (Number.isFinite(tempHp)) updated.tempHp = tempHp;
        return updated;
      };

      const updatedTarget = { ...target };
      updatedTarget.name = target?.name; // preserve name in case nested overwrite occurs
      // Apply to top-level and common nested holders
      Object.assign(updatedTarget, applyTo({ ...updatedTarget }));
      if (updatedTarget.data) updatedTarget.data = applyTo({ ...updatedTarget.data });
      if (updatedTarget.characterData) updatedTarget.characterData = applyTo({ ...updatedTarget.characterData });
      if (updatedTarget.monsterData) updatedTarget.monsterData = applyTo({ ...updatedTarget.monsterData });
      if (updatedTarget.entity) updatedTarget.entity = applyTo({ ...updatedTarget.entity });

      debugLog(`[CombatManager] HP_APPLY_DEBUG ${target?.name || 'Target'} via ${chosen.key}: -${damage} → ${newCurrent}/${newMax} (temp ${tempHp})`);

      return updatedTarget;
    } catch (error) {
      console.error('[CombatManager] Error applying damage:', error);
      return target;
    }
  }

  /**
   * Apply healing to a target entity with D&D 5e rules
   * @param {object} target - The target entity
   * @param {number} healing - The amount of healing to apply
   * @returns {object} - Updated target entity
   *
   * D&D 5e Rule: Healing restores hit points but cannot exceed maximum HP
   * PHB 2024: "A creature cannot regain hit points above its hit point maximum"
   * @date_of_change: 2025-09-19 - Added healing application for healing spells
   * @date_of_change: 2026-08-31 - Require structured combat HP and keep preview resolution immutable.
   */
  static applyHealing(target, healing) {
    if (!target || typeof target !== 'object') {
      throw new Error('Healing target must be a structured combatant object. See Character_Monster_Data_Architecture.md.');
    }

    const currentHp = Number(target?.hpObj?.current);
    const maxHp = Number(target?.hpObj?.max);
    if (!Number.isFinite(currentHp) || !Number.isFinite(maxHp) || currentHp < 0 || maxHp < 1 || currentHp > maxHp) {
      throw new Error(
        `Healing target ${target.id || target.name || 'unknown'} has invalid target.hpObj; expected { current: 0..max, max: positive number }. See Character_Monster_Data_Architecture.md.`
      );
    }

    const requestedHealing = Number(healing);
    if (!Number.isFinite(requestedHealing) || requestedHealing < 0) {
      throw new Error('Healing amount must be a finite non-negative number.');
    }

    // A client-side result is a preview until the dedicated server mutation
    // commits it. Clone nested HP so preview calculation cannot leak into the
    // authoritative token before healing:apply runs. D&D 5e healing changes
    // current HP only and never exceeds the maximum. date_of_change: 2026-08-31
    const newCurrentHp = Math.min(maxHp, currentHp + requestedHealing);
    const actualHealing = newCurrentHp - currentHp;
    const updated = {
      ...target,
      hpObj: {
        ...target.hpObj,
        current: newCurrentHp,
        max: maxHp
      },
      hp: newCurrentHp,
      maxHp
    };

    debugLog(`[CombatManager] 💚 ${target.name || 'Target'} healed for ${actualHealing} HP (${newCurrentHp}/${maxHp})`);

    return updated;
  }

  /**
   * Parse multiattack action using MultiattackManager for comprehensive structured approach
   * D&D 5e Rule: Multiattack can include abilities (like Frightful Presence) + attacks
   * @param {object} multiattackAction - The multiattack action with text description or structured data
   * @param {array} allActions - All available actions for the creature
   * @param {array} allTraits - All available traits/abilities for the creature
   * @param {object} creature - The creature performing the multiattack (for level-based calculations)
   * @returns {array} - Array of component actions to execute (attacks and abilities)
   * date_of_change: 2025-09-05 - Integrated MultiattackManager for comprehensive structured approach
   */
  static parseMultiattack(multiattackAction, allActions = [], allTraits = [], creature = {}) {
    try {
      // Import MultiattackManager for structured resolution
      const MultiattackManager = require('../services/MultiattackManager.js').default;
      
      debugLog(`[CombatManager] Using MultiattackManager to resolve: ${multiattackAction.name}`);
      
      // Use MultiattackManager for comprehensive resolution
      const components = MultiattackManager.resolveMultiattack(
        multiattackAction, 
        allActions, 
        allTraits, 
        creature
      );
      
      if (components && components.length > 0) {
        // Add CombatManager-specific metadata
        const enhancedComponents = components.map(component => ({
          ...component,
          isMultiattackComponent: true,
          parentMultiattack: multiattackAction.name
        }));
        
        debugLog(`[CombatManager] MultiattackManager resolved ${enhancedComponents.length} components: ${enhancedComponents.map(c => c.name).join(', ')}`);
        return enhancedComponents;
      } else {
        debugLog(`[CombatManager] MultiattackManager returned no components for: ${multiattackAction.name}`);
        return [];
      }
      
    } catch (error) {
      console.error(`[CombatManager] Error using MultiattackManager, falling back to legacy parsing:`, error);
      // Fallback to legacy parsing if MultiattackManager fails
      return this.parseMultiattackLegacy(multiattackAction, allActions, allTraits);
    }
  }

  /**
   * Legacy text parsing for multiattack (preserved for backward compatibility)
   * @param {object} multiattackAction - The multiattack action with text description  
   * @param {array} allActions - All available actions for the creature
   * @param {array} allTraits - All available traits/abilities for the creature
   * @returns {array} - Array of component actions to execute (attacks and abilities)
   * date_of_change: 2025-09-05 - Renamed from original parseMultiattack to maintain fallback
   */
  static parseMultiattackLegacy(multiattackAction, allActions = [], allTraits = []) {
    try {
      const text = multiattackAction.text || multiattackAction.description || multiattackAction.desc || '';
      
      
      // Fallback to text parsing for legacy data
      if (!text) {
        debugLog('[CombatManager] Multiattack action has no text description or structured components');
        return [];
      }
      
      debugLog(`[CombatManager] Using text parsing for multiattack: "${text}"`);
      
      // D&D 5e multiattack can include abilities + attacks
      const componentActions = [];
      
      // First, look for abilities mentioned before attacks (e.g., "Frightful Presence")
      const abilityNames = ['frightful presence', 'terrifying howl', 'roar', 'gaze', 'aura', 'breath'];
      for (const abilityName of abilityNames) {
        if (text.toLowerCase().includes(abilityName)) {
          // Look in both actions and traits for this ability
          let matchingAbility = allActions.find(action => 
            (action.name || '').toLowerCase().includes(abilityName)
          );
          
          if (!matchingAbility) {
            matchingAbility = allTraits.find(trait => 
              (trait.name || '').toLowerCase().includes(abilityName)
            );
          }
          
          if (matchingAbility) {
            componentActions.push({
              ...matchingAbility,
              isAbility: true, // Mark as ability for different handling
              requiresSave: true // Most multiattack abilities require saves
            });
            debugLog(`[CombatManager] Found multiattack ability: ${matchingAbility.name}`);
          }
        }
      }
      
      // Then look for attack patterns
      const attackNames = [];
      
      // More precise patterns to avoid false matches
      // Look for "with its [attack name]" pattern (e.g., "one with its bite")  
      const withItsPattern = /(?:one|two|three|1|2|3)?\s*with its (\w+)/gi;
      let match;
      while ((match = withItsPattern.exec(text)) !== null) {
        const attackName = match[1].toLowerCase();
        // Only add if it's a known attack name to avoid false matches
        const commonAttacks = ['bite', 'claw', 'claws', 'slam', 'fist', 'tail', 'sting', 'beak', 'horn', 'tusk', 'wing', 'tentacle'];
        if (commonAttacks.includes(attackName)) {
          attackNames.push(attackName);
        }
      }
      
      // Look for specific patterns like "one bite and two claw attacks"
      // Skip broad patterns like "three attacks" that don't specify the attack type
      const specificPatterns = [
        /one (\w+) and two (\w+)/gi,  // "one bite and two claw attacks"
        /makes (?:a |an |one )?(\w+) attack/gi  // "makes a bite attack"
      ];
      
      for (const pattern of specificPatterns) {
        pattern.lastIndex = 0; // Reset regex state
        while ((match = pattern.exec(text)) !== null) {
          const attackName = match[1].toLowerCase();
          const commonAttacks = ['bite', 'claw', 'claws', 'slam', 'fist', 'tail', 'sting', 'beak', 'horn', 'tusk', 'wing', 'tentacle'];
          if (commonAttacks.includes(attackName)) {
            attackNames.push(attackName);
          }
        }
      }
      
      // Special handling for the common "one with its X and two with its Y" pattern
      const oneAndTwoPattern = /one with its (\w+) and two with its (\w+)/gi;
      if ((match = oneAndTwoPattern.exec(text)) !== null) {
        const firstAttack = match[1].toLowerCase();
        const secondAttack = match[2].toLowerCase();
        const commonAttacks = ['bite', 'claw', 'claws', 'slam', 'fist', 'tail', 'sting', 'beak', 'horn', 'tusk', 'wing', 'tentacle'];
        
        if (commonAttacks.includes(firstAttack)) {
          attackNames.push(firstAttack);
        }
        if (commonAttacks.includes(secondAttack)) {
          // Add twice for "two"
          attackNames.push(secondAttack);  
          attackNames.push(secondAttack);
        }
      }
      
      debugLog(`[CombatManager] Raw attack names found: ${JSON.stringify(attackNames)}`);
      debugLog(`[CombatManager] Parsing text: "${text}"`);
      
      // Match found attack names to actual actions
      for (const attackName of attackNames) {
        const matchingAction = allActions.find(action => {
          const actionName = (action.name || '').toLowerCase();
          return actionName === attackName || 
                 actionName.includes(attackName) ||
                 attackName.includes(actionName.split(' ')[0]); // Handle "Claw" vs "Claws"
        });
        
        if (matchingAction && !matchingAction.isAbility) {
          // Ensure it's actually an attack action by checking attackType or attack property
          const isAttackAction = matchingAction.attackType === 'melee' || 
                                 matchingAction.attackType === 'ranged' || 
                                 matchingAction.attack || 
                                 matchingAction.attackBonus !== undefined;
          
          if (isAttackAction) {
            componentActions.push({
              ...matchingAction,
              isAttack: true // Mark as attack for attack roll handling
            });
            debugLog(`[CombatManager] Matched "${attackName}" to action: ${matchingAction.name}`);
          } else {
            debugLog(`[CombatManager] Found matching action "${matchingAction.name}" but it's not an attack action`);
          }
        } else {
          debugLog(`[CombatManager] Could not find action for attack: ${attackName}`);
        }
      }
      
      // If we couldn't parse specific attacks, try to extract count and use available attacks
      if (componentActions.filter(a => a.isAttack).length === 0) {
        const numberPattern = /(two|three|four|2|3|4)/i;
        const numberMatch = text.match(numberPattern);
        
        if (numberMatch) {
          const countMap = { 'two': 2, 'three': 3, 'four': 4, '2': 2, '3': 3, '4': 4 };
          const count = countMap[numberMatch[1].toLowerCase()] || 2;
          
          // Use first available attack actions - check both attack property and attackType
          const attackActions = allActions.filter(action => 
            (action.attack || action.attackType) && action.name !== 'Multiattack'
          );
          
          for (let i = 0; i < Math.min(count, attackActions.length); i++) {
            componentActions.push({
              ...attackActions[i],
              isAttack: true
            });
          }
          
          debugLog(`[CombatManager] Parsed multiattack count: ${count}, using available attacks`);
        }
      }
      
      // Fallback: if still no attacks found, use all available attack actions (max 3)
      if (componentActions.filter(a => a.isAttack).length === 0) {
        const attackActions = allActions.filter(action => 
          (action.attack || action.attackType) && action.name !== 'Multiattack'
        ).slice(0, 3);
        
        attackActions.forEach(action => {
          componentActions.push({
            ...action,
            isAttack: true
          });
        });
        debugLog('[CombatManager] Fallback: using all available attack actions for multiattack');
      }
      
      debugLog(`[CombatManager] Multiattack components: ${componentActions.map(a => a.name).join(', ')}`);
      return componentActions;
      
    } catch (error) {
      console.error('[CombatManager] Error parsing multiattack:', error);
      return [];
    }
  }

  /**
   * Find all targets within range of an area effect ability
   * @param {object} caster - The entity using the ability
   * @param {object} ability - The ability with area effect
   * @param {Array} potentialTargets - All possible targets in the battle
   * @param {object} battleState - Current battle state
   * @returns {Array} - Array of targets within range
   * date_of_change: 2025-09-01 - Added for area effect abilities
   */
  static findTargetsInRange(caster, ability, potentialTargets = [], battleState = {}) {
    try {
      // @date_of_change: 2025-10-13 - POLICY: No heuristics; require structured range and grid context
      const range = Number(ability.range ?? ability.areaRange);
      if (!Number.isFinite(range)) {
        throw new Error('[CombatManager.findTargetsInRange] Missing structured ability.range/areaRange');
      }
      if (!battleState?.gridType || !Number.isFinite(battleState?.feetPerCell) || !Number.isFinite(battleState?.gridSize)) {
        throw new Error('[CombatManager.findTargetsInRange] Missing grid context (gridType/feetPerCell/gridSize)');
      }
      
      debugLog(`[CombatManager] Finding targets within ${range} feet of ${caster.name} for ${ability.name}`);
      
      const targetsInRange = [];
      const casterPos = RangeCalculator.getTokenCenter(caster, { gridSize: battleState.gridSize, feetPerCell: battleState.feetPerCell });
      
      for (const target of potentialTargets) {
        // Skip self
        if (target.id === caster.id) continue;
        
        const targetPos = RangeCalculator.getTokenCenter(target, { gridSize: battleState.gridSize, feetPerCell: battleState.feetPerCell });
        const distance = RangeCalculator.calculateGridDistance(
          casterPos,
          targetPos,
          battleState.gridType,
          battleState.feetPerCell
        );
        
        if (distance.feet <= range) {
          targetsInRange.push({
            target,
            distance: distance.feet
          });
          debugLog(`[CombatManager] ${target.name} is within range (${distance.feet} feet)`);
        } else {
          debugLog(`[CombatManager] ${target.name} is out of range (${distance.feet} feet > ${range} feet)`);
        }
      }
      
      return targetsInRange;
      
    } catch (error) {
      console.error('[CombatManager] Error finding targets in range:', error);
      return [];
    }
  }

  /**
   * Execute saving throw for abilities like Frightful Presence
   * D&D 5e Rule: Saving throws use d20 + ability modifier + proficiency (if proficient)
   * @param {object} target - The target making the save
   * @param {object} ability - The ability requiring the save
   * @param {object} options - Additional options (advantage, disadvantage, manualRolls)
   * @returns {object} - Result object with save details and effects
   * date_of_change: 2025-09-01 - Added for multiattack abilities with saves
   */
  static executeSavingThrow(target, ability, options = {}) {
    try {
      // @date_of_change 2025-09-17 - ENHANCED: Support parsing saving throw data from description text
      // Extract save info from description if not explicitly provided
      let saveInfo = ability.save || ability.savingThrow;
      let saveAbility = 'dexterity'; // Common default for AoE abilities
      let saveDC = 15; // Fallback DC

      if (!saveInfo && ability.desc) {
        // Parse save information from description text (e.g., "DC 24 Dexterity saving throw")
        const saveMatch = ability.desc.match(/dc (\d+) (\w+) saving throw/i);
        if (saveMatch) {
          saveDC = parseInt(saveMatch[1]);
          saveAbility = saveMatch[2].toLowerCase();
          debugLog(`[CombatManager] 🔍 Parsed save from description: DC ${saveDC} ${saveAbility}`);
        }
      } else if (saveInfo) {
        saveAbility = saveInfo.ability || saveAbility;
        saveDC = saveInfo.dc || saveDC;
      }

      // @date_of_change 2025-09-18 - CRITICAL FIX: Proper ability score access and save proficiency
      // Get target's ability modifier - check multiple data paths for canonical format
      const getTargetAbilities = () => {
        return target.abilities ||
               target.entity?.abilities ||
               target.canonicalData?.abilities ||
               target.monsterData?.abilities ||
               target.characterData?.abilities ||
               target.data?.abilities ||
               {};
      };

      const targetAbilities = getTargetAbilities();

      // @date_of_change 2025-09-18 - Handle both canonical (strength) and abbreviated (str) ability names
      const getAbilityScore = (ability) => {
        const abilityMap = {
          'dexterity': ['dexterity', 'dex'],
          'strength': ['strength', 'str'],
          'constitution': ['constitution', 'con'],
          'intelligence': ['intelligence', 'int'],
          'wisdom': ['wisdom', 'wis'],
          'charisma': ['charisma', 'cha']
        };

        const possibleNames = abilityMap[ability] || [ability];
        for (const name of possibleNames) {
          if (targetAbilities[name] !== undefined) {
            return targetAbilities[name];
          }
        }
        return 10; // Default ability score
      };

      const abilityScore = getAbilityScore(saveAbility);
      const abilityMod = Math.floor((abilityScore - 10) / 2);

      console.log(`[CombatManager] 🔍 ${target.name} ability data:`, {
        targetAbilities,
        saveAbility,
        abilityScore,
        abilityMod,
        targetDataKeys: Object.keys(target)
      });

      // @date_of_change 2025-09-18 - CRITICAL FIX: Only add proficiency if proficient in the save
      // Check for save proficiency in canonical savingThrows field
      const getSavingThrows = () => {
        return target.savingThrows ||
               target.entity?.savingThrows ||
               target.canonicalData?.savingThrows ||
               target.monsterData?.savingThrows ||
               target.characterData?.savingThrows ||
               target.data?.savingThrows ||
               {};
      };

      const savingThrows = getSavingThrows();

      // Map ability names to potential saving throw keys
      const saveKeyMap = {
        'dexterity': ['dex', 'dexterity'],
        'strength': ['str', 'strength'],
        'constitution': ['con', 'constitution'],
        'intelligence': ['int', 'intelligence'],
        'wisdom': ['wis', 'wisdom'],
        'charisma': ['cha', 'charisma']
      };

      // Check if target is proficient in this saving throw
      let saveProficiencyBonus = 0;
      const saveKeys = saveKeyMap[saveAbility] || [saveAbility];

      for (const key of saveKeys) {
        if (savingThrows[key] !== undefined) {
          // If there's a specific save bonus, use it directly (it includes ability mod + proficiency)
          const totalSaveBonus = savingThrows[key];
          const expectedWithJustAbilityMod = abilityMod;
          saveProficiencyBonus = totalSaveBonus - expectedWithJustAbilityMod;
          console.log(`[CombatManager] 🛡️ ${target.name} found ${key} save proficiency: total bonus ${totalSaveBonus}, ability mod ${abilityMod}, proficiency bonus ${saveProficiencyBonus}`);
          break;
        }
      }

      // @date_of_change: 2025-10-13 - Manual dice bridge
      // Roll the save (with advantage/disadvantage if specified), honoring manual mode.
      const manualRolls = Array.isArray(options?.manualRolls) && options.manualRolls.length > 0
        ? options.manualRolls
        : this.requestManualD20Rolls(target, {
          advantage: !!options.advantage,
          disadvantage: !!options.disadvantage,
          label: `${target?.name || 'Target'} ${saveAbility} save`
        });
      const saveD20 = this.rollD20({
        advantage: !!options.advantage,
        disadvantage: !!options.disadvantage,
        manualRolls: manualRolls || null
      });
      const finalRoll = saveD20.value;

      // date_of_change: 2025-10-25 - Effects: Bless/Bane on saving throws
      // Bless: add 1d4 to saves; Bane: subtract 1d4 from saves
      let extraDice = [];
      try {
        const se = target?.statusEffects || {};
        if (se.bless) {
          const blessRoll = this.rollDice('1d4', false, null, { roller: target, label: 'Bless saving throw modifier' });
          extraDice.push({ source: 'Bless', formula: '1d4', total: blessRoll.total, rolls: blessRoll.rolls });
        }
        if (se.bane) {
          const baneRoll = this.rollDice('1d4', false, null, { roller: target, label: 'Bane saving throw modifier' });
          extraDice.push({ source: 'Bane', formula: '-1d4', total: -baneRoll.total, rolls: baneRoll.rolls });
        }
      } catch (_) {}

      const extraSum = extraDice.reduce((s, d) => s + d.total, 0);
      const total = finalRoll + abilityMod + saveProficiencyBonus + extraSum;
      const saveSuccess = total >= saveDC;

      console.log(`[CombatManager] 🛡️ ${target.name} ${saveAbility} save: ${finalRoll} + ${abilityMod} + ${saveProficiencyBonus} = ${total} vs DC ${saveDC} (${saveSuccess ? 'SUCCESS' : 'FAILURE'})`);

      // @date_of_change 2025-09-17 - CRITICAL FIX: Handle damage for save-or-suck abilities
      let damageResult = null;
      let updatedTarget = target;

      if (ability.damage && Array.isArray(ability.damage) && ability.damage.length > 0) {
        // Roll damage using existing damage rolling logic
        const damageRoll = ability.damage[0];
        const rollResult = this.rollDice(damageRoll.formula || `${damageRoll.dice}d${damageRoll.sides || 6}`, false, null, {
          roller: target,
          label: `${ability?.name || 'Saving throw'} damage`
        });
        const damageTotal = rollResult.total;

        // Apply full damage on failed save, half damage on successful save (D&D 5e standard)
        let finalDamage = saveSuccess ? Math.floor(damageTotal / 2) : damageTotal;

        // @date_of_change 2025-09-17 - Apply damage resistances and immunities per D&D 5e rules
        const damageType = damageRoll.type || 'untyped';
        let damageModifier = '';

        // @date_of_change 2025-09-18 - CRITICAL FIX: Check multiple data paths for immunities/resistances
        // Target data can be in different structures: direct, entity, canonicalData, monsterData, etc.
        const getTargetProperty = (propName) => {
          return target[propName] ||
                 target.entity?.[propName] ||
                 target.canonicalData?.[propName] ||
                 target.monsterData?.[propName] ||
                 target.characterData?.[propName] ||
                 target.data?.[propName] ||
                 [];
        };

        // @date_of_change: 2025-10-14 - Also read nested damageModifiers.* from monster data
        const damageModifiers = (
          target.damageModifiers ||
          target.entity?.damageModifiers ||
          target.canonicalData?.damageModifiers ||
          target.monsterData?.damageModifiers ||
          target.monsterData?.data?.damageModifiers ||
          target.characterData?.damageModifiers ||
          target.data?.damageModifiers ||
          {}
        );
        const immunities = getTargetProperty('damageImmunities') || damageModifiers.immunities;
        const resistances = getTargetProperty('damageResistances') || damageModifiers.resistances;
        const vulnerabilities = getTargetProperty('damageVulnerabilities') || damageModifiers.vulnerabilities;

        console.log(`[CombatManager] 🛡️ Checking ${target.name} immunities/resistances for ${damageType}:`, {
          immunities,
          resistances,
          vulnerabilities,
          targetKeys: Object.keys(target)
        });

        const toLowerList = (list) => Array.isArray(list) ? list.map(v => String(v).toLowerCase()) : [];
        const immLower = toLowerList(immunities);
        const resLower = toLowerList(resistances);
        const vulLower = toLowerList(vulnerabilities);
        const damageTypeKey = String(damageType || 'untyped').toLowerCase();

        if (immLower.includes(damageTypeKey)) {
          finalDamage = 0;
          damageModifier = ' (IMMUNE)';
          console.log(`[CombatManager] 🛡️ ${target.name} is IMMUNE to ${damageTypeKey} damage`);
        } else if (resLower.includes(damageTypeKey)) {
          finalDamage = Math.floor(finalDamage / 2);
          damageModifier = ' (RESISTANT)';
          console.log(`[CombatManager] 🛡️ ${target.name} is RESISTANT to ${damageTypeKey} damage`);
        } else if (vulLower.includes(damageTypeKey)) {
          finalDamage = finalDamage * 2;
          damageModifier = ' (VULNERABLE)';
          console.log(`[CombatManager] 🛡️ ${target.name} is VULNERABLE to ${damageTypeKey} damage`);
        } else {
          console.log(`[CombatManager] 🛡️ ${target.name} has no special resistance to ${damageTypeKey} damage`);
        }

        console.log(`[CombatManager] 💥 ${ability.name} damage: ${damageTotal} ${damageType} (${saveSuccess ? 'half on save' : 'full on failed save'})${damageModifier} = ${finalDamage} damage to ${target.name}`);

        // Apply damage to target's HP
        const currentHp = target.hp || target.hpObj?.current || 0;
        const maxHp = target.maxHp || target.hpObj?.max || currentHp || 1; // Ensure we have a valid maxHp
        const newHp = Math.max(0, currentHp - finalDamage);

        console.log(`[CombatManager] HP calculation: currentHp=${currentHp}, finalDamage=${finalDamage}, newHp=${newHp}, maxHp=${maxHp}`);

        // Update target HP directly
        updatedTarget = {
          ...target,
          hp: newHp,
          hpObj: {
            ...target.hpObj,
            current: newHp,
            max: maxHp
          }
        };

        damageResult = {
          total: finalDamage,
          originalTotal: damageTotal,
          type: damageRoll.type || 'untyped',
          formula: rollResult.formula,
          rolls: rollResult.rolls,
          halfDamage: saveSuccess,
          resistanceApplied: damageModifier
        };
      }

      return {
        success: true, // Save execution succeeded (regardless of save result)
        saveSuccess, // Whether the actual saving throw succeeded
        target: target.name,
        targetId: target.id,
        updatedTarget,
        savingThrow: {
          d20Roll: finalRoll,
          modifier: abilityMod + saveProficiencyBonus,
          total,
          dc: saveDC,
          ability: saveAbility,
          advantage: options.advantage,
          disadvantage: options.disadvantage,
          extraDice
        },
        damage: damageResult,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('[CombatManager] Error executing saving throw:', error);
      return { success: false, reason: 'Save execution failed', error: error.message };
    }
  }

  /**
   * Preview the advantage/disadvantage and Sneak Attack adjudication for an
   * attack WITHOUT rolling dice or mutating any state.
   *
   * This is the single source of truth for the pre-roll derivation: executeAttack
   * calls it before rolling, and the Action Targeting popup calls it to show the
   * player what will apply and why. Keeping one derivation avoids UI/engine drift.
   *
   * D&D 5e Rules References:
   * - Advantage/Disadvantage and cancellation: PHB p.173 (2014) / 2024 PHB ch.1
   * - Unseen attackers and targets: PHB p.194-195
   * - Conditions granting or imposing modifiers: PHB Appendix A
   * - Prone: advantage within 5 ft, disadvantage beyond: PHB p.292
   * - Long range disadvantage: PHB p.147
   * - Sneak Attack: PHB p.96 (2014) / 2024 PHB Rogue
   *
   * Note: results are the expected adjudication given current, visible state.
   * Execution re-derives with the same logic; GM overrides and state changes
   * between preview and execution can alter the final outcome.
   *
   * @param {object} attacker - The attacking entity (token/combatant)
   * @param {object} target - The target entity (token/combatant)
   * @param {object} action - The attack action being considered
   * @param {object} battleState - Battle state (tokens, gridSize, gridType, feetPerCell)
   * @param {object} options - Explicit modifiers (advantage/disadvantage) and grid options
   * @returns {object} - { resolvedAction, attackTypeHint, validation, advantage,
   *   disadvantage, netAdvantage, netDisadvantage, advantageSources,
   *   disadvantageSources, unseenAttack, unseenAttackSource, distanceFeet,
   *   adjacentQualifyingCreature, sneakAttack }
   * date_of_change: 2026-07-11 - Extracted from executeAttack for targeting preview
   */
  static previewAttackConditions(attacker, target, action, battleState = {}, options = {}) {
    const resolvedAction = buildActionResolutionView(action, {
      abilityModifierForAbility: (ability) => CombatManager.getAbilityModifier(attacker, ability)
    });

    // Provide explicit attackType hint to RangeCalculator (no heuristics there)
    let attackTypeHint = null;
    if (typeof resolvedAction?.attackType === 'string') {
      const t = resolvedAction.attackType.toLowerCase();
      if (t === 'melee' || t === 'ranged') attackTypeHint = t;
    }
    if (!attackTypeHint && typeof resolvedAction?.attack?.type === 'string') {
      const tt = resolvedAction.attack.type.toLowerCase();
      if (tt.includes('melee')) attackTypeHint = 'melee';
      else if (tt.includes('ranged')) attackTypeHint = 'ranged';
    }
    if (!attackTypeHint && (Number.isFinite(resolvedAction?.reach) || Number.isFinite(resolvedAction?.attack?.reach))) {
      attackTypeHint = 'melee';
    }
    if (!attackTypeHint && (resolvedAction?.range != null || resolvedAction?.attack?.range != null)) {
      attackTypeHint = 'ranged';
    }

    const validation = RangeCalculator.validateAttack(attacker, target, resolvedAction, battleState, { ...options, attackType: attackTypeHint });

    const hiddenStealthTotal = Number(attacker?.combatFlags?.hiddenStealthCheck?.total);
    const targetPassivePerception = resolvePassivePerception(target);
    const hiddenFromTarget = Number.isFinite(hiddenStealthTotal)
      && Number.isFinite(targetPassivePerception)
      && hiddenStealthTotal > targetPassivePerception
      && collectHiddenFrom(attacker).some((observerId) => (
        matchesEntityIdentifier(observerId, target)
      ));
    // A durable Hidden condition is already an authoritative adjudication. Hide actions
    // additionally retain their Stealth-versus-passive-Perception audit data when present.
    const authoritativeHidden = hasCombatCondition(attacker, ['Hidden']);
    const attackerIsUnseen = hiddenFromTarget || authoritativeHidden || hasCombatCondition(attacker, ['Invisible', 'Unseen']);
    const targetCannotSee = hasCombatCondition(target, ['Blinded']);
    const unseenAttack = attackerIsUnseen || targetCannotSee;
    const attackerConditionDisadvantage = hasCombatCondition(attacker, ['Blinded', 'Poisoned', 'Prone', 'Restrained']);
    const targetIsUnseen = hasCombatCondition(target, ['Hidden', 'Invisible', 'Unseen']);
    const targetGrantsAdvantage = hasCombatCondition(target, ['Blinded', 'Paralyzed', 'Restrained', 'Stunned', 'Unconscious']);
    // Dodge is persisted as a turn-scoped combat flag by the action engine.
    // Read that canonical snapshot in addition to explicit conditions so the
    // button's promised disadvantage is actually enforced.
    // date_of_change: 2026-07-18
    const targetIsDodging = target?.combatFlags?.dodging === true
      || hasCombatCondition(target, ['Dodge', 'Dodging']);
    const targetIsProne = hasCombatCondition(target, ['Prone']);
    const attackerDistanceFeet = distanceFeetBetween(attacker, target, battleState);
    const distanceKnown = Number.isFinite(attackerDistanceFeet);
    const proneAdvantage = targetIsProne && distanceKnown && attackerDistanceFeet <= 5;
    const proneDisadvantage = targetIsProne && distanceKnown && attackerDistanceFeet > 5;
    const adjacentQualifyingCreature = qualifyingCreatureNextToTarget(attacker, target, battleState);
    // SRD 5.1 Pack Tactics uses the embedded trait's explicit distance.
    // date_of_change: 2026-09-05
    const packTacticsAdvantage = Array.isArray(attacker.traits) && attacker.traits.some(trait =>
      trait.mechanics?.kind === 'adjacentAllyAdvantage'
      && Boolean(qualifyingCreatureNextToTarget(attacker, target, battleState, {
        requireAttackerAlly: true, distanceFeet: trait.mechanics.distanceFeet
      })));
    const steadyAimAdvantage = attacker?.combatFlags?.steadyAimActive === true;
    const targetCanSeeAttacker = !targetCannotSee && !attackerIsUnseen;

    const advantageSources = [
      unseenAttack ? (authoritativeHidden ? 'Hidden attacker' : 'unseen attacker') : null,
      targetGrantsAdvantage ? 'target condition' : null,
      proneAdvantage ? 'prone target within 5 feet' : null,
      packTacticsAdvantage ? 'Pack Tactics' : null,
      steadyAimAdvantage ? 'Steady Aim' : null
    ].filter(Boolean);
    const disadvantageSources = [
      validation.hasDisadvantage ? 'range' : null,
      attackerConditionDisadvantage ? 'attacker condition' : null,
      targetIsUnseen ? 'unseen target' : null,
      targetIsDodging && targetCanSeeAttacker ? 'target is Dodging' : null,
      proneDisadvantage ? 'prone target beyond 5 feet' : null
    ].filter(Boolean);

    const advantage = Boolean(options.advantage) || advantageSources.length > 0;
    const disadvantage = Boolean(options.disadvantage) || disadvantageSources.length > 0;
    // rollD20 cancels advantage and disadvantage against each other (PHB p.173).
    const netAdvantage = advantage && !disadvantage;
    const netDisadvantage = disadvantage && !advantage;

    // --- Sneak Attack preview (same gating executeAttack applies on a hit) ---
    const nameStr = String(resolvedAction?.attack?.type || resolvedAction?.attackType || '').toLowerCase();
    const isWeaponAttack = nameStr.includes('weapon') || resolvedAction?.type === 'weapon' || resolvedAction?.type === 'attack' || !!resolvedAction?.attack;
    const props = []
      .concat(Array.isArray(resolvedAction?.properties) ? resolvedAction.properties : [])
      .concat(Array.isArray(resolvedAction?.attack?.properties) ? resolvedAction.attack.properties : []);
    const hasFinesse = props.some((property) => (
      String(property?.name ?? property?.label ?? property).trim().toLowerCase() === 'finesse'
    ));
    const isRangedAttack = nameStr.includes('ranged') || attackTypeHint === 'ranged';

    const featurePresent = sneakAttackFeaturePresent(attacker);
    const diceCount = featurePresent ? sneakAttackDiceCount(attacker) : 0;

    const roundNow = Number(battleState?.round ?? battleState?.currentRound ?? CombatLogManager?.currentRound) || 1;
    const turnNow = Number(battleState?.turn ?? battleState?.currentTurn ?? CombatLogManager?.currentTurn) || 0;
    // Sneak Attack usage is persisted on the canonical token snapshot. The
    // singleton action manager must not be a second source of combat truth.
    // rule_reference: 2024 PHB Rogue — Sneak Attack is once per turn.
    // rule_reference: AGENTS.md — session snapshot is authoritative.
    // date_of_change: 2026-07-18
    const alreadyUsedThisTurn = featurePresent && (
      options.sneakAttackAlreadyUsed === true
      || sneakAttackUsedInSnapshot(attacker, roundNow, turnNow)
    );

    const sneakReasons = [];
    const sneakBlockers = [];
    if (featurePresent) {
      if (!isWeaponAttack) sneakBlockers.push('not a weapon attack');
      else if (!hasFinesse && !isRangedAttack) sneakBlockers.push('weapon is neither finesse nor ranged');
      if (netDisadvantage) sneakBlockers.push('attack has disadvantage');
      if (alreadyUsedThisTurn) sneakBlockers.push('already used this turn');
      if (netAdvantage) {
        sneakReasons.push(`advantage on the attack (${advantageSources.join(', ') || 'explicit advantage'})`);
      }
      if (adjacentQualifyingCreature) {
        sneakReasons.push(`enemy adjacent to target: ${adjacentQualifyingCreature?.name ?? adjacentQualifyingCreature?.id ?? 'qualifying creature'}`);
      }
      if (!netAdvantage && !adjacentQualifyingCreature) {
        sneakBlockers.push('no advantage and no other enemy within 5 feet of the target');
      }
    }
    const sneakEligible = featurePresent
      && diceCount > 0
      && isWeaponAttack
      && (hasFinesse || isRangedAttack)
      && !netDisadvantage
      && !alreadyUsedThisTurn
      && (netAdvantage || Boolean(adjacentQualifyingCreature));

    const cunningSelection = normalizeCunningStrikeSelection(resolvedAction?.cunningStrike);
    const cunningFeaturePresent = hasNamedFeature(attacker, 'Cunning Strike');
    const selectedCunningStrike = cunningSelection.valid ? cunningSelection.selection : null;
    const cunningBlockers = [];
    if (selectedCunningStrike && !cunningFeaturePresent) cunningBlockers.push('attacker does not have Cunning Strike');
    if (selectedCunningStrike && !sneakEligible) cunningBlockers.push('Sneak Attack is not eligible');
    if (selectedCunningStrike && diceCount < selectedCunningStrike.diceCost) {
      cunningBlockers.push(`requires ${selectedCunningStrike.diceCost} Sneak Attack die`);
    }
    const cunningValid = cunningSelection.valid && cunningBlockers.length === 0;
    const cunningDiceCost = cunningValid && selectedCunningStrike ? selectedCunningStrike.diceCost : 0;
    const sneakDamageDiceCount = Math.max(0, diceCount - cunningDiceCost);

    return {
      resolvedAction,
      attackTypeHint,
      validation,
      advantage,
      disadvantage,
      netAdvantage,
      netDisadvantage,
      advantageSources: [...(options.advantage ? ['explicit advantage'] : []), ...advantageSources],
      disadvantageSources: [...(options.disadvantage ? ['explicit disadvantage'] : []), ...disadvantageSources],
      unseenAttack,
      targetCanSeeAttacker,
      unseenAttackSource: hiddenFromTarget
        ? 'hiddenFromTarget'
        : (authoritativeHidden ? 'hiddenCondition' : (attackerIsUnseen ? 'attackerCondition' : (targetCannotSee ? 'targetBlinded' : null))),
      hiddenFromTarget,
      hiddenStealthTotal: hiddenFromTarget ? hiddenStealthTotal : null,
      targetPassivePerception: hiddenFromTarget ? targetPassivePerception : null,
      distanceFeet: distanceKnown ? attackerDistanceFeet : null,
      adjacentQualifyingCreature,
      sneakAttack: {
        featurePresent,
        diceCount,
        dice: diceCount > 0 ? `${diceCount}d6` : null,
        damageDiceCount: sneakDamageDiceCount,
        damageDice: sneakDamageDiceCount > 0 ? `${sneakDamageDiceCount}d6` : null,
        weaponAttack: isWeaponAttack,
        finesseOrRanged: hasFinesse || isRangedAttack,
        alreadyUsedThisTurn,
        eligible: sneakEligible,
        reasons: sneakReasons,
        blockers: sneakBlockers
      },
      cunningStrike: {
        featurePresent: cunningFeaturePresent,
        valid: cunningValid,
        selected: selectedCunningStrike,
        diceCost: cunningDiceCost,
        blockers: [
          ...(cunningSelection.valid ? [] : [cunningSelection.error]),
          ...cunningBlockers
        ]
      }
    };
  }

  /**
   * Execute attack with full D&D 5e mechanics
   * @param {object} attacker - The attacking entity
   * @param {object} target - The target entity
   * @param {object} action - The attack action being used
   * @param {object} battleState - Current battle state for validation
   * @param {object} options - Additional options
   * @returns {object} - Result object with all attack details and updated target
   * date_of_change: 2025-08-31 - Enhanced with proper D&D 5e mechanics
   * date_of_change: 2026-07-11 - Pre-roll derivation delegated to previewAttackConditions
   */
  static executeAttack(attacker, target, action, battleState = {}, options = {}) {
    try {
      const attackRules = CombatManager.resolveAttackRules(battleState);
      // @date_of_change 2026-07-11 - Pre-roll adjudication (advantage/disadvantage sources,
      // Sneak Attack gating) now comes from previewAttackConditions, which the Action
      // Targeting popup also calls. One derivation, zero UI/engine drift.
      const preview = CombatManager.previewAttackConditions(attacker, target, action, battleState, options);
      const resolvedAction = preview.resolvedAction;
      if (resolvedAction?.cunningStrike && preview.cunningStrike?.valid !== true) {
        throw new Error(`Cunning Strike cannot be applied: ${preview.cunningStrike?.blockers?.join('; ') || 'invalid selection'}`);
      }

      // @date_of_change 2025-09-13 - CRITICAL DEBUG: Track duplicate execution calls
      const executionId = `${attacker.name}-${resolvedAction?.name || action?.name}-${Date.now()}`;
      console.log(`⚔️ [COMBAT_EXECUTION_TRACK] ${executionId}: ${attacker.name} attacks ${target.name} with ${resolvedAction?.name || action?.name}`);
      
      // Initialize result object
      // @date_of_change 2025-09-13 - CRITICAL FIX: Add success property for ActionEconomyProcessor compatibility
      const result = {
        success: true, // Always true for completed attack execution (hit or miss)
        attacker: attacker.name,
        attackerId: attacker.id,
        target: target.name,
        targetId: target.id,
        action: resolvedAction?.name || action?.name,
        attackRoll: null,
        damage: null,
        updatedTarget: null,
        timestamp: new Date().toISOString()
      };
      
      // @date_of_change 2025-09-15 - CRITICAL FIX: Add range validation to apply disadvantage for long-range attacks
      // @date_of_change 2026-07-11 - Range validation, attack-type hint, hidden/condition
      // adjudication, and advantage/disadvantage sources are derived once in
      // previewAttackConditions and reused here.
      const validation = preview.validation;
      const unseenAttack = preview.unseenAttack;
      const adjacentQualifyingCreature = preview.adjacentQualifyingCreature;
      const distanceKnown = Number.isFinite(preview.distanceFeet);
      const attackerDistanceFeet = distanceKnown ? preview.distanceFeet : Infinity;

      // Merge hidden-attacker advantage and range disadvantage with explicit modifiers.
      const attackOptions = {
        ...options,
        attackRules,
        disadvantage: preview.disadvantage,
        // rollD20 handles advantage/disadvantage cancellation.
        advantage: preview.advantage
      };

      console.log(`[CombatManager] Range validation for ${resolvedAction?.name || action?.name}: inRange=${validation.valid}, hasDisadvantage=${validation.hasDisadvantage}, distance=${validation.rangeInfo?.distance}ft`);

      // Calculate attack roll with D&D 5e mechanics (including range disadvantage)
      const attackRoll = this.calculateAttackRoll(attacker, target, resolvedAction, attackOptions);
      result.attackRoll = attackRoll;
      result.hiddenAttack = unseenAttack;
      result.unseenAttack = unseenAttack;
      result.targetCanSeeAttacker = preview.targetCanSeeAttacker;
      result.unseenAttackSource = preview.unseenAttackSource;
      result.advantageSources = preview.advantageSources;
      result.disadvantageSources = preview.disadvantageSources;
      result.adjacentQualifyingCreature = adjacentQualifyingCreature
        ? { id: adjacentQualifyingCreature.id, name: adjacentQualifyingCreature.name }
        : null;
      result.hiddenStealthCheck = preview.hiddenFromTarget
        ? (attacker?.combatFlags?.hiddenStealthCheck || null)
        : null;
      result.hiddenStealthTotal = preview.hiddenStealthTotal;
      result.targetPassivePerception = preview.targetPassivePerception;
      result.rangeInfo = validation.rangeInfo; // Include range information in result

      // Paralyzed and unconscious creatures suffer a critical hit from any attack that
      // hits from within 5 feet, regardless of whether the attack itself is melee or ranged.
      if (attackRoll.isHit
        && distanceKnown
        && attackerDistanceFeet <= 5
        && hasCombatCondition(target, ['Paralyzed', 'Unconscious'])) {
        attackRoll.isCritical = true;
        attackRoll.conditionCritical = true;
      }
      
      // If attack hits, calculate and apply damage (only when damage metadata exists)
      if (attackRoll.isHit) {
        const hasDamageInfo = (Array.isArray(resolvedAction?.damage) && resolvedAction.damage.length > 0)
          || isNonEmptyString(resolvedAction?.damageFormula)
          || isNonEmptyString(resolvedAction?.damage)
          || (Array.isArray(resolvedAction?.attack?.damage) && resolvedAction.attack.damage.length > 0)
          || isNonEmptyString(resolvedAction?.attack?.damageFormula)
          || isNonEmptyString(resolvedAction?.attack?.damage);
        if (hasDamageInfo) {
          result.damage = this.calculateDamage(resolvedAction, attackRoll.isCritical, {
            attacker,
            roller: attacker,
            label: `${resolvedAction?.name || 'Attack'} damage`,
            manualRollQueue: options?.manualRollQueue,
            criticalDiceMultiplier: attackRules.criticalHit.damageDiceMultiplier,
            damageRollModifier: attackRules.damageRollModifier
          });
        } else {
          result.damage = null; // Non-damaging attack (e.g., shove); skip damage pipeline
        }

        // Sneak Attack integration (characters and monsters with the feature)
        // @date_of_change 2026-07-11 - Weapon/finesse/feature checks shared with
        // previewAttackConditions; the advantage/disadvantage gate stays on the
        // actual attackRoll flags so GM roll overrides remain authoritative.
        if (result.damage) try {
          const canApplySA = (() => {
            // Must be a finesse or ranged weapon attack (not a spell damage roll)
            if (!preview.sneakAttack.weaponAttack || !preview.sneakAttack.finesseOrRanged) return false;

            // Disadvantage negates SA (even if ally nearby)
            if (attackRoll.disadvantage) return false;

            // Advantage or another non-incapacitated enemy of the target within 5 feet.
            return attackRoll.advantage || Boolean(adjacentQualifyingCreature);
          })();

          // Feature present and not already used this turn
          const hasSAFeature = preview.sneakAttack.featurePresent;

          const roundNow = Number(battleState?.round ?? battleState?.currentRound ?? CombatLogManager?.currentRound) || 1;
          const turnNow = Number(battleState?.turn ?? battleState?.currentTurn ?? CombatLogManager?.currentTurn) || 0;
          const alreadyUsed = options.sneakAttackAlreadyUsed === true
            || sneakAttackUsedInSnapshot(attacker, roundNow, turnNow);

          if (hasSAFeature && canApplySA && !alreadyUsed && result.damage) {
            const baseDiceCount = sneakAttackDiceCount(attacker);
            const diceCount = Number(preview.sneakAttack?.damageDiceCount ?? baseDiceCount);
            if (baseDiceCount > 0) {
              const baseType = (result?.damage?.details?.[0]?.damageType) || (Array.isArray(resolvedAction?.attack?.damage) && resolvedAction.attack.damage[0]?.type) || resolvedAction?.damageType || 'piercing';
              let sneakDamageTotal = 0;
              if (diceCount > 0) {
                const saDamage = this.calculateDamage({ name: 'Sneak Attack', damage: [{ dice: `${diceCount}d6`, type: baseType }] }, attackRoll.isCritical, {
                  roller: attacker,
                  label: 'Sneak Attack damage',
                  // The Cunning Strike cost is removed before rolling; a
                  // critical hit doubles only the remaining Sneak Attack dice.
                  // rule_reference: 2024 PHB Rogue — Cunning Strike.
                  // date_of_change: 2026-07-18
                  manualRollQueue: options?.manualRollQueue,
                  criticalDiceMultiplier: attackRules.criticalHit.damageDiceMultiplier
                });
                sneakDamageTotal = saDamage.total;
                result.damage.total += saDamage.total;
                if (Array.isArray(result.damage.details)) {
                  result.damage.details.push({
                    formula: `${diceCount}d6`,
                    damageType: baseType,
                    rolls: saDamage.baseRolls || saDamage.rolls,
                    total: saDamage.total,
                    isCritical: !!attackRoll.isCritical,
                    source: 'Sneak Attack'
                  });
                }
              }
              result.sneakAttack = {
                dice: diceCount > 0 ? `${diceCount}d6` : '0d6',
                total: sneakDamageTotal
              };
              if (preview.cunningStrike?.selected) {
                result.sneakAttack.baseDice = `${baseDiceCount}d6`;
                result.sneakAttack.diceSpent = Number(preview.cunningStrike.diceCost || 0);
              }
              result.sneakAttackQualification = attackRoll.advantage
                ? 'advantage'
                : `enemy adjacent to target: ${adjacentQualifyingCreature?.name ?? adjacentQualifyingCreature?.id ?? 'qualifying creature'}`;
              if (preview.cunningStrike?.selected) {
                const dcResult = resolveCunningStrikeSaveDC(attacker);
                if (!dcResult.valid) throw new Error(dcResult.error);
                result.cunningStrike = {
                  effect: preview.cunningStrike.selected.key,
                  label: preview.cunningStrike.selected.label,
                  diceCost: preview.cunningStrike.selected.diceCost,
                  saveAbility: preview.cunningStrike.selected.saveAbility,
                  saveDC: dcResult.dc,
                  pendingResolution: true
                };
              }
            }
          }
        } catch (saErr) {
          // A selected Cunning Strike changes both damage dice and downstream
          // state. Continuing after its resolution fails would commit a
          // different attack than the player confirmed.
          // rule_reference: AGENTS.md — no partial/silent combat commits.
          // date_of_change: 2026-07-18
          if (preview.cunningStrike?.selected || resolvedAction?.cunningStrike) throw saErr;
          console.warn('[CombatManager] Sneak Attack evaluation failed:', saErr?.message || saErr);
        }

        // @date_of_change: 2025-10-27 - Per-component mitigation for normal attacks
        if (result.damage) {
          const preMitigationTotal = Number(result.damage?.total);
          // Extra weapon/trait dice are one hit. Combine like damage types
          // before rounding resistance, retaining each roll in the audit.
          // SRD 5.1 damage resistance; date_of_change: 2026-09-05
          const details = resolvedAction.mechanics?.kind === 'attack'
            ? groupCreatureDamageDetails(result.damage?.details || []) : result.damage?.details || [];
          const mitigation = CombatManager.applyMitigationToDetails(target, details);
          const finalDamage = mitigation.total;
          const typesSet = new Set((mitigation.details || []).map(d => d.damageType));

          // @date_of_change: 2025-10-28 - Ensure combat results report mitigated totals while preserving originals
          result.damage.originalTotal = Number.isFinite(preMitigationTotal) ? preMitigationTotal : mitigation.originalTotal;
          result.damage.total = finalDamage;
          result.damage.mitigatedTotal = finalDamage;
          result.damage.resistanceApplied = mitigation.summary;
          result.damage.details = mitigation.details;
          result.damage.type = (typesSet.size === 1 ? [...typesSet][0] : 'mixed');

          // Apply damage with robust HP detection across token/entity/character/monster paths
          const hpPaths = {
            topLevel: { hp: typeof target?.hp, hpObj: !!target?.hpObj, maxHp: typeof target?.maxHp },
            entity: target?.entity ? { hp: typeof target.entity.hp, hpObj: !!target.entity.hpObj } : null,
            characterData: target?.characterData ? { hp: typeof target.characterData.hp, hpObj: !!target.characterData.hpObj } : null,
            monsterData: target?.monsterData ? { hp: typeof target.monsterData.hp, hpObj: !!target.monsterData.hpObj } : null,
            data: target?.data ? { hp: typeof target.data.hp, hpObj: !!target.data.hpObj, maxHp: typeof target.data.maxHp } : null
          };
          console.log('[CombatManager] HP_DETECTION_DEBUG', { target: target?.name, hpPaths });

          result.updatedTarget = this.applyDamage(target, finalDamage);
        }
        
      }
      
      return result;
    } catch (error) {
      console.error('[CombatManager] Error executing attack:', error);
      return {
        success: false, // @date_of_change 2025-09-13 - CRITICAL: Add success property for error handling
        error: true,
        message: error.message,
        attacker: attacker?.name || 'unknown',
        target: target?.name || 'unknown',
        action: action?.name || 'unknown'
      };
    }
  }

  /**
   * Execute a spell with area of effect
   * @param {object} caster
   * @param {object} spell
   * @param {object} targetPoint {x,y}
   * @param {Array} potentialTargets
   * @param {object} battleState
   * @param {object} options
   * @returns {object} AoE result
   * date_of_change: 2025-08-30
   */
  static executeSpell(caster, spell, targetPoint, potentialTargets = [], battleState = {}, options = {}) {
    spell = buildActionResolutionView(spell);
    const attackRules = CombatManager.resolveAttackRules(battleState);
    console.log(
      `[CombatManager] ${caster.name} casts ${spell.name} at (${targetPoint?.x ?? "?"}, ${targetPoint?.y ?? "?"})`
    );

    const normalizedAreaOfEffect = normalizeAreaOfEffect(spell?.areaOfEffect);
    const shape = String(normalizedAreaOfEffect?.shape || '').toLowerCase();
    const isDirectionalAoE = shape === 'line' || shape === 'cone';
    // @date_of_change: 2026-04-03 - Keep explicit creature-target spells on the same range geometry as ActionEconomyProcessor.
    // rule_reference: D&D 5e targeted spells validate each chosen creature against range; legal Bless targets must not fail because center-to-center distance exceeds edge distance.
    const hasExplicitSelectedTargets = !normalizedAreaOfEffect && Array.isArray(potentialTargets) && potentialTargets.length > 0;
    const sanitizeGridToken = (token) => {
      if (!token) return token;
      const gxRaw = token.gridX;
      const gyRaw = token.gridY;
      const gx = (typeof gxRaw === 'string') ? (gxRaw.trim().toLowerCase() === 'off grid' ? NaN : Number(gxRaw)) : gxRaw;
      const gy = (typeof gyRaw === 'string') ? (gyRaw.trim().toLowerCase() === 'off grid' ? NaN : Number(gyRaw)) : gyRaw;

      return {
        ...token,
        gridX: Number.isFinite(gx) ? gx : token.gridX,
        gridY: Number.isFinite(gy) ? gy : token.gridY,
        size: token.size || 'Medium'
      };
    };
    const resolveSpellRangeFeet = () => {
      if (Number.isFinite(options?.meta?.range)) return Number(options.meta.range);
      if (Number.isFinite(spell?.range)) return Number(spell.range);
      if (Number.isFinite(spell?.rangeFeet)) return Number(spell.rangeFeet);

      const rawRange = typeof spell?.range === 'string'
        ? spell.range
        : spell?.rangeText;

      if (typeof rawRange === 'string') {
        const loweredRange = rawRange.trim().toLowerCase();
        if (loweredRange === 'self' || loweredRange.startsWith('self')) return 0;
        if (loweredRange === 'touch') return 5;

        const parsedRange = RangeCalculator.parseRangeValue(rawRange);
        if (Number.isFinite(parsedRange)) return parsedRange;
      }

      return null;
    };

      let rangeValidation;
    if (isDirectionalAoE) {
      // Directional templates originate from the caster's edge in the given direction.
      // For 'self' range spells (e.g., "Self (100-foot line)"), inRange is inherently true.
      const gridSize = Number(options?.gridSize);
      const feetPerCell = Number(options?.feetPerCell);
      const direction = Number(options?.direction) || 0;
      if (!Number.isFinite(gridSize) || !Number.isFinite(feetPerCell)) {
        throw new Error('[CombatManager.executeSpell] Missing grid context for directional AoE (gridSize/feetPerCell)');
      }

      const origin = RangeCalculator.getDirectionalAoEOrigin(caster, direction, { gridSize });

      let spellRangeFeet;
      if (typeof spell?.range === 'string') {
        const lower = spell.range.toLowerCase();
        if (lower === 'self' || lower.startsWith('self')) {
          spellRangeFeet = 0;
        } else if (lower === 'touch') {
          spellRangeFeet = 5;
        } else {
          const numeric = Number.parseFloat(spell.range);
          spellRangeFeet = Number.isFinite(numeric) ? numeric : 0;
        }
      } else if (Number.isFinite(spell?.range)) {
        spellRangeFeet = Number(spell.range);
      } else if (Number.isFinite(spell?.rangeFeet)) {
        spellRangeFeet = Number(spell.rangeFeet);
      } else {
        spellRangeFeet = 0;
      }

      let inRange = true;
      let distanceInfo = null;
      if (spellRangeFeet > 0 && targetPoint) {
        const casterCenter = RangeCalculator.getTokenCenter(caster, { gridSize, feetPerCell });
        const dist = RangeCalculator.calculateGridDistance(casterCenter, targetPoint, options.gridType, feetPerCell);
        distanceInfo = dist;
        inRange = dist.feet <= spellRangeFeet;
      }

      let affectedArea = [];
      if (shape === 'line') {
        const lengthFeet = Number(normalizedAreaOfEffect?.length ?? normalizedAreaOfEffect?.size ?? 0);
        const widthFeet = Number(normalizedAreaOfEffect?.width ?? normalizedAreaOfEffect?.thickness ?? 5);
        affectedArea = RangeCalculator.calculateLineAreaCells(
          origin.edge.grid,
          direction,
          lengthFeet,
          widthFeet,
          { feetPerCell }
        );
      } else if (shape === 'cone') {
        const sizeFeet = Number(normalizedAreaOfEffect?.size ?? 0);
        affectedArea = RangeCalculator.calculateConeAreaCells(
          origin.edge.grid,
          direction,
          sizeFeet,
          { feetPerCell }
        );
      }

      rangeValidation = {
        inRange,
        distance: distanceInfo?.feet ?? 0,
        spellRange: spellRangeFeet,
        distanceInfo,
        affectedArea
      };
    } else if (hasExplicitSelectedTargets) {
      const spellRangeFeet = resolveSpellRangeFeet();
      if (!Number.isFinite(spellRangeFeet)) {
        rangeValidation = {
          inRange: false,
          distance: null,
          spellRange: null,
          distanceInfo: null,
          affectedArea: [],
          targetChecks: [],
          reason: 'Invalid or missing spell.range (require number, "self", or "touch")'
        };
      } else {
        const targetRangeOptions = {
          ...options,
          gridType: options?.gridType ?? battleState?.gridType,
          feetPerCell: options?.feetPerCell ?? battleState?.feetPerCell,
          gridSize: options?.gridSize ?? battleState?.gridSize
        };
        const sanitizedCaster = sanitizeGridToken(caster);
        const targetChecks = potentialTargets.map((target) => {
          if (!target) {
            return {
              targetId: null,
              target: 'unknown',
              inRange: false,
              distance: null,
              distanceInfo: null,
              reason: 'Missing selected target',
              selfTarget: false
            };
          }

          if (target.id === caster?.id) {
            return {
              targetId: target.id,
              target: target.name,
              inRange: true,
              distance: 0,
              distanceInfo: { feet: 0, cells: 0 },
              selfTarget: true
            };
          }

          const sanitizedTarget = sanitizeGridToken(target);
          const targetRangeCheck = RangeCalculator.checkRangedRange(
            sanitizedCaster,
            sanitizedTarget,
            { maxRange: spellRangeFeet },
            targetRangeOptions
          );

          return {
            targetId: target.id,
            target: target.name,
            inRange: !!targetRangeCheck?.inRange,
            distance: targetRangeCheck?.distance ?? null,
            distanceInfo: targetRangeCheck?.distanceInfo ?? null,
            reason: targetRangeCheck?.reason,
            selfTarget: false
          };
        });
        const checkedDistances = targetChecks
          .map((check) => check.distance)
          .filter((distance) => Number.isFinite(distance));
        const firstRelevantCheck = targetChecks.find((check) => !check.inRange) || targetChecks.find((check) => Number.isFinite(check.distance)) || null;
        const failedChecks = targetChecks.filter((check) => !check.inRange);

        rangeValidation = {
          inRange: targetChecks.every((check) => check.inRange),
          distance: checkedDistances.length > 0 ? Math.max(...checkedDistances) : 0,
          spellRange: spellRangeFeet,
          distanceInfo: firstRelevantCheck?.distanceInfo ?? null,
          affectedArea: [],
          targetChecks,
          reason: failedChecks.length > 0
            ? failedChecks.map((check) => `${check.target}: ${check.reason || 'Target out of spell range'}`).join('; ')
            : null
        };
      }
    } else {
      // Non-directional shapes (sphere/circle/cube/square) use the generic spell range calculator
      const spellForRange = normalizedAreaOfEffect
        ? { ...spell, areaOfEffect: normalizedAreaOfEffect }
        : spell;
      rangeValidation = RangeCalculator.calculateSpellRange(caster, spellForRange, targetPoint, options);
      // If UI specified a targeting mode, recalculate affectedArea with that precision to match overlay
      if (normalizedAreaOfEffect && options?.targetMode && options?.gridType && Number.isFinite(options?.feetPerCell)) {
        try {
          const adjustedArea = RangeCalculator.calculateAreaOfEffect(
            targetPoint,
            normalizedAreaOfEffect,
            options.gridType,
            options.feetPerCell,
            options.targetMode
          );
          rangeValidation.affectedArea = adjustedArea;
        } catch (e) {
          console.warn('[CombatManager] Non-directional AoE area recalculation failed:', e?.message);
        }
      }
    }

      // Self-cast bypass: ensure inRange for self-range spells
      if (options && options.selfCast === true) {
        try {
          if (!rangeValidation || typeof rangeValidation !== 'object') {
            rangeValidation = { inRange: true, distance: 0, spellRange: 0 };
          } else {
            rangeValidation.inRange = true;
            rangeValidation.distance = 0;
            if (!Number.isFinite(rangeValidation.spellRange)) {
              rangeValidation.spellRange = 0;
            }
          }
        } catch (_) {}
      }

      const result = {
        success: true, // @date_of_change 2025-09-12 - Add success flag for ActionEconomyProcessor
        caster: caster.name,
        casterId: caster.id,
        spell: spell.name,
        targetPoint,
        rangeValidation,
        affectedTargets: [],
        timestamp: new Date().toISOString()
      };

      if (!rangeValidation.inRange) {
        console.log(`[CombatManager] Spell out of range: ${rangeValidation.distance} > ${rangeValidation.spellRange}`);
        result.success = false; // @date_of_change 2025-09-12 - Mark failed spells as unsuccessful
        result.error = 'Spell target is out of range';
        return result;
      }

      if (Array.isArray(rangeValidation.affectedArea) && rangeValidation.affectedArea.length > 0) {
        // @date_of_change: 2025-10-14 - AOE footprint detection: include tokens if ANY occupied cell is in affectedArea
        const areaSet = new Set(rangeValidation.affectedArea.map(c => `${c.x},${c.y}`));
        // @date_of_change: 2025-10-14 - Candidate source: prefer provided potentialTargets; fallback to battleState.tokens
        const candidateTargets = (Array.isArray(potentialTargets) && potentialTargets.length > 0)
          ? potentialTargets
          : (Array.isArray(battleState?.tokens) ? battleState.tokens : []);
        if (!candidateTargets.length) {
          console.warn('[CombatManager] AoE has affectedArea but no candidate targets provided and no tokens in battleState');
        } else if (!Array.isArray(potentialTargets) || potentialTargets.length === 0) {
          console.log('[CombatManager] Using battleState.tokens as AoE candidate set');
        }
        for (const tgt of candidateTargets) {
          try {
            const cells = RangeCalculator.getTokenCells(tgt, options);
            const inArea = cells.some(c => areaSet.has(`${c.x},${c.y}`));
            if (inArea) {
              result.affectedTargets.push({
                target: tgt.name,
                targetId: tgt.id,
                originalTarget: tgt,
                distance: 0
              });
            }
          } catch (cellErr) {
            // @date_of_change: 2025-10-14 - No fallbacks policy: record error, do not substitute center-test
            result.errors = result.errors || [];
            result.errors.push(`AoE footprint computation failed for target ${tgt?.name || tgt?.id}: ${cellErr?.message || cellErr}`);
            console.error('[CombatManager] AoE footprint error:', { target: tgt, error: cellErr });
          }
        }
        console.log(`[CombatManager] AOE footprint inclusion → ${result.affectedTargets.length} targets`);
      } else {
        // @date_of_change 2025-09-12 - Enhanced targeted spell handling for Magic Missile and similar spells
        
        // For targeted spells (like Magic Missile) and creature-targeted buffs (e.g., Bless),
        // use all provided targets if valid
        if (potentialTargets && potentialTargets.length > 0 && 
            (hasExplicitSelectedTargets ||
             (options?.meta?.kind === 'buff') ||
             spell.name?.toLowerCase().includes('magic missile') || 
             spell.damage)) { // Any damaging spell can be targeted
          
          // Use all provided targets for targeted spells
          for (const tgt of potentialTargets) {
            if (tgt && tgt.name) {
              result.affectedTargets.push({
                target: tgt.name,
                targetId: tgt.id,
                originalTarget: tgt,
                distance: 0 // Distance irrelevant for targeted spells
              });
            }
          }
        } else {
          // Single point spell: pick a target within 5 ft of point
          let closest = null;
          let closestFeet = Infinity;
          for (const tgt of potentialTargets) {
            const pos = RangeCalculator.getTokenCenter(tgt, options);
            const dist = RangeCalculator.calculateGridDistance(targetPoint, pos, options.gridType, options.feetPerCell);
            if (dist.feet < closestFeet && dist.feet <= 5) {
              closest = tgt;
              closestFeet = dist.feet;
            }
          }
          if (closest) {
            result.affectedTargets.push({
              target: closest.name,
              targetId: closest.id,
              originalTarget: closest,
              distance: closestFeet
            });
          }
        }
      }

      // @date_of_change: 2025-09-18 - DEBUG: Check spell structure for saving throw detection
      console.log(`[CombatManager] 🔍 SPELL_DEBUG - Checking spell structure for ${spell.name}:`, {
        attackType: spell.attackType,
        saveType: spell.saveType,
        saveDC: spell.saveDC,
        damage: !!spell.damage,
        healing: !!spell.healing,
        healingFormula: spell.healing,
        spellKeys: Object.keys(spell),
        fullSpell: spell
      });

      // Apply spell damage and effects
      // @date_of_change: 2025-09-14 - Magic Missile special handling per RAW (PHB):
      // The spell creates three glowing darts of magical force (at 1st level).
      // Each dart hits automatically and deals 1d4+1 force damage. Higher Levels:
      // +1 dart per slot level above 1st. Darts can be divided among targets.
      if (result.affectedTargets.length > 0) {
        // Preserve the immutable pre-resolution HP scalar before any local or
        // authoritative token commit can replace/mutate the referenced target.
        // The combat log must never reconstruct a revival's before-state from
        // a post-heal token object.
        // date_of_change: 2026-08-20
        result.affectedTargets.forEach((targetInfo) => {
          const hpBefore = targetInfo?.originalTarget?.hpObj?.current ?? targetInfo?.originalTarget?.hp;
          if (hpBefore !== null && hpBefore !== undefined && Number.isFinite(Number(hpBefore))) {
            targetInfo.targetHpBefore = Number(hpBefore);
          }
        });

        // @date_of_change: 2025-09-14 - Prefer structured meta when available (from SpellHandler.resolve)
        const isMagicMissile = (options?.meta?.kind === 'auto_hit_multi_dart') || String(spell?.name || '').toLowerCase().includes('magic missile');

        // date_of_change: 2025-09-18 - Consolidate saving throw detection to a single boolean and log inputs
        const hasExplicitSave = !!(spell.attackType === 'save' && spell.saveType) || !!(spell.save || spell.savingThrow);
        const spellDescription = String(spell.desc || spell.description || '');
        const descMentionsSave = /(strength|dexterity|constitution|intelligence|wisdom|charisma)\s+saving throw/i.test(spellDescription)
          || /dc\s*\d+[^.]{0,40}saving throw/i.test(spellDescription);
        // @date_of_change 2025-09-18 - TREAT areaOfEffect PRESENCE AS AOE even if affectedArea array is empty
        // This covers cases where the AoE origin point isn't available to the engine,
        // but the UI has already provided the concrete target list from an AoE selection.
        const hasComputedArea = Array.isArray(rangeValidation.affectedArea) && rangeValidation.affectedArea.length > 0;
        const declaresAoE = !!normalizedAreaOfEffect;
        const isAoE = hasComputedArea || declaresAoE;
        const hasDamage = !!spell.damage;
        const hasAttackRoll = !!(spell.toHit || typeof spell.attackBonus === 'number' || spell.attack?.toHit || spell.requiresAttackRoll === true);
        const inferSaveForAoE = isAoE && hasDamage && !hasAttackRoll;
        const useSavingThrows = !isMagicMissile && (hasExplicitSave || descMentionsSave || inferSaveForAoE);

        console.log('[CombatManager] 🔎 AOE_SAVE_DETECT', {
          spellName: spell.name,
          hasExplicitSave,
          descMentionsSave,
          isAoE,
          hasDamage,
          hasAttackRoll,
          inferSaveForAoE,
          useSavingThrows
        });

        if (isMagicMissile) {
          const slotLevel = Number(options?.spellLevel ?? spell?.level ?? 1) || 1;
          const totalDarts = 3 + Math.max(0, slotLevel - 1);
          const allocation = {};
          const selected = result.affectedTargets
            .map((tInfo) => tInfo.targetId)
            .filter(Boolean);

          if (options?.dartAllocation && typeof options.dartAllocation === 'object') {
            let assigned = 0;
            for (const tid of selected) {
              const count = Math.max(0, Math.floor(Number(options.dartAllocation[tid] || 0)));
              allocation[tid] = count;
              assigned += count;
            }

            let remaining = Math.max(0, totalDarts - assigned);
            let idx = 0;
            while (remaining > 0 && selected.length > 0) {
              const tid = selected[idx % selected.length];
              allocation[tid] = (allocation[tid] || 0) + 1;
              remaining -= 1;
              idx += 1;
            }
          } else if (selected.length > 0) {
            const base = Math.floor(totalDarts / selected.length);
            let remainder = totalDarts % selected.length;
            for (const tid of selected) {
              allocation[tid] = base + (remainder > 0 ? 1 : 0);
              if (remainder > 0) {
                remainder -= 1;
              }
            }
          }

          for (const tInfo of result.affectedTargets) {
            const dartsForThisTarget = Math.max(0, allocation[tInfo.targetId] || 0);
            let total = 0;
            const dartDetails = [];
            // @date_of_change: 2025-10-14 - Magic Missile uses 1d4+1 force per dart (RAW).
            // Prefer structured meta from SpellHandler.resolve; fall back to 1d4+1 force if not present.
            const mmDiceSpec = (options?.meta?.multiComponent?.perComponent?.formula) || '1d4+1';
            const mmType = (options?.meta?.multiComponent?.perComponent?.type) || 'force';

            for (let i = 0; i < dartsForThisTarget; i += 1) {
              const dmg = this.calculateDamage({
                name: `${spell?.name || 'Magic Missile'} dart`,
                type: 'spell',
                spellLevel: slotLevel,
                damage: [{ dice: mmDiceSpec, type: mmType }]
              }, false, {
                roller: caster,
                label: `${spell?.name || 'Magic Missile'} dart damage`
              });
              total += dmg.total;
              dartDetails.push({
                index: i + 1,
                total: dmg.total,
                formula: dmg.formula,
                rolls: dmg.baseRolls,
                modifier: dmg.modifier,
                type: (dmg.details?.[0]?.damageType) || mmType
              });
            }

            const aggregated = {
              total,
              formula: `${mmDiceSpec} x ${dartsForThisTarget}`,
              rolls: dartDetails.flatMap((detail) => detail.rolls || []),
              details: dartDetails.map((detail) => ({
                formula: detail.formula,
                damageType: detail.type,
                rolls: detail.rolls,
                total: detail.total,
                isCritical: false
              }))
            };

            tInfo.darts = dartsForThisTarget;
            tInfo.damage = aggregated;
            tInfo.damage.originalTotal = total;
            const mitigation = CombatManager.applyMitigationToDetails(tInfo.originalTarget, tInfo.damage.details || []);
            tInfo.damage.mitigatedTotal = mitigation.total;
            tInfo.damage.resistanceApplied = mitigation.summary;
            tInfo.damage.details = mitigation.details;
            tInfo.damage.total = mitigation.total;
            tInfo.updatedTarget = this.applyDamage(tInfo.originalTarget, mitigation.total);
          }

          result.magicMissile = {
            slotLevel,
            totalDarts,
            allocation
          };
        } else if (useSavingThrows) {
          // @date_of_change: 2025-09-18 - Route saving throw spells through proper save system
          console.log(`[CombatManager] Processing saving throw spell: ${spell.name} (${spell.saveType} save)`);

          // Create saving throw results for each affected target
          const saveResults = [];
          result.affectedTargets.forEach(tInfo => {
            // date_of_change: 2025-09-18 - CRITICAL FIX
            // executeSavingThrow expects an ability-like object with either `save`/`savingThrow` or `desc`.
            // Build a compatible object from the spell so we don't rely on fragile free-text parsing.
            let abilityForSave;
            if (spell.save || spell.savingThrow) {
              abilityForSave = spell;
            } else {
              // Try to infer from fields or description
              let inferredAbility = (spell.saveType || 'dexterity');
              // date_of_change: 2025-09-18 - Prefer caster's spell save DC when available
              const casterSpellcasting = this.getSpellcastingStats(caster);
              const casterSpellDC = (
                caster?.spells?.saveDC ||
                caster?.spellcasting?.saveDC ||
                caster?.spellcasting?.spellSaveDC ||
                caster?.characterData?.spells?.saveDC ||
                caster?.characterData?.spellcasting?.saveDC ||
                caster?.characterData?.spellcasting?.spellSaveDC ||
                caster?.canonicalData?.spells?.saveDC ||
                caster?.canonicalData?.spellcasting?.saveDC ||
                caster?.canonicalData?.spellcasting?.spellSaveDC ||
                caster?.data?.spells?.saveDC ||
                caster?.data?.spellcasting?.saveDC ||
                caster?.data?.spellcasting?.spellSaveDC ||
                casterSpellcasting.saveDC ||
                null
              );
              let inferredDC = (spell.saveDC || casterSpellDC || 15);
              const descText = String(spell.desc || spell.description || '');
              const m = descText.match(/dc\s*(\d+)\s*(strength|dexterity|constitution|intelligence|wisdom|charisma)\s*saving throw/i);
              if (m) {
                inferredDC = parseInt(m[1], 10) || inferredDC;
                inferredAbility = m[2].toLowerCase();
              } else {
                const abilityMatch = descText.match(/\b(strength|dexterity|constitution|intelligence|wisdom|charisma)\s+saving throw\b/i);
                if (abilityMatch) inferredAbility = abilityMatch[1].toLowerCase();
              }
              abilityForSave = { ...spell, save: { ability: inferredAbility, dc: inferredDC } };
            }

            const saveResult = this.executeSavingThrow(
              tInfo.originalTarget,
              abilityForSave,
              {}
            );

            // @date_of_change: 2025-10-14 - Persist per-target save outcome for downstream condition gating
            tInfo.saveResult = saveResult;
            tInfo.saveSuccess = !!saveResult.saveSuccess;

            // Apply damage based on save result with immunity/resistance checks
            const hasDamageInfo = Array.isArray(spell?.damage) || typeof spell?.damage === 'string' || typeof spell?.damageFormula === 'string' || Array.isArray(spell?.attack?.damage);
            if (hasDamageInfo) {
              let baseDamage;
              try {
                baseDamage = this.calculateDamage(spell, false, {
                  roller: caster,
                  label: `${spell?.name || 'Spell'} damage`
                });
              } catch (e) { console.error('[CombatManager] calculateDamage failed (save spell):', e?.message || e); baseDamage = null; }
              if (!baseDamage || !Array.isArray(baseDamage.details)) {
                console.warn(`[CombatManager] Save spell ${spell?.name} provided damage metadata but calculation failed; skipping damage`);
                return;
              }
              // Do not infer damage type; leave 'untyped' to allow GM override pipeline to resolve
              const detailsWithType = baseDamage.details;

              const explicitSuccessDamage = String(
                spell?.save?.damageOnSuccess
                ?? spell?.savingThrow?.damageOnSuccess
                ?? spell?.damageOnSuccessfulSave
                ?? ''
              ).toLowerCase();
              const halfOnSuccess = spell?.halfDamageOnSave === true
                || explicitSuccessDamage === 'half'
                || /half as much damage|half damage on (?:a )?successful save/i.test(spellDescription);
              const noDamageOnSuccess = spell?.halfDamageOnSave === false
                || explicitSuccessDamage === 'none'
                || explicitSuccessDamage === 'zero';

              // Most save spells deal no damage on success unless their rules explicitly say half.
              const preMitigate = detailsWithType.map(d => ({
                ...d,
                total: tInfo.saveSuccess
                  ? (halfOnSuccess && !noDamageOnSuccess ? Math.floor((Number(d.total) || 0) / 2) : 0)
                  : (Number(d.total) || 0)
              }));
              const mitigation = CombatManager.applyMitigationToDetails(tInfo.originalTarget, preMitigate);
              tInfo.updatedTarget = this.applyDamage(tInfo.originalTarget, mitigation.total);
              const typesSet = new Set(preMitigate.map(d => d.damageType));
              tInfo.damage = {
                total: mitigation.total,
                originalTotal: baseDamage.total,
                preMitigationTotal: mitigation.originalTotal,
                type: (typesSet.size === 1 ? [...typesSet][0] : 'mixed'),
                formula: baseDamage.formula || spell.damage,
                resistanceApplied: mitigation.summary,
                details: mitigation.details,
                mitigatedTotal: mitigation.total
              };
            } else {
              // No damage block on a damage save spell: surface explicit error
              console.warn(`[CombatManager] Saving throw spell ${spell?.name} had no structured damage; skipping damage application`);
            }

            // Store save results for combat log
            saveResults.push({
              target: tInfo.target,
              targetId: tInfo.targetId,
              savingThrow: saveResult,
              damage: tInfo.damage,
              saveSuccess: saveResult.saveSuccess
            });
          });

          // Store save results for ActionEconomyProcessor
          result.results = saveResults;

        // @date_of_change: 2025-10-14 - Attack-roll spells (e.g., Guiding Bolt, Scorching Ray single-ray)
        } else if (hasAttackRoll) {
          const attackSpellResults = [];
          for (const tInfo of result.affectedTargets) {
            const spellcasting = this.getSpellcastingStats(caster);
            const spellAbility = spellcasting.ability || (
              caster?.spells?.ability ||
              caster?.spellcasting?.ability ||
              caster?.spellcasting?.spellcastingAbility ||
              caster?.characterData?.spells?.ability ||
              caster?.characterData?.spellcasting?.ability ||
              caster?.characterData?.spellcasting?.spellcastingAbility ||
              caster?.canonicalData?.spells?.ability ||
              caster?.canonicalData?.spellcasting?.ability ||
              caster?.canonicalData?.spellcasting?.spellcastingAbility ||
              caster?.data?.spells?.ability ||
              caster?.data?.spellcasting?.ability ||
              caster?.data?.spellcasting?.spellcastingAbility ||
              'intelligence'
            );
            const actionForAttack = {
              name: spell?.name,
              type: 'spell',
              attackType: 'ranged',
              attack: { type: 'ranged spell attack', ability: spellAbility },
              damage: spell?.damage,
              damageType: spell?.damageType
            };

            const atk = this.calculateAttackRoll(caster, tInfo.originalTarget, actionForAttack, { attackRules });
            const entry = { target: tInfo.target, targetId: tInfo.targetId, attackRoll: atk };
            if (atk.isHit) {
              const dmg = this.calculateDamage(spell, atk.isCritical, {
                roller: caster,
                label: `${spell?.name || 'Spell'} damage`,
                criticalDiceMultiplier: attackRules.criticalHit.damageDiceMultiplier,
                damageRollModifier: attackRules.damageRollModifier
              });
              const mitigation = CombatManager.applyMitigationToDetails(tInfo.originalTarget, dmg.details || []);
              const typesSet = new Set((dmg.details || []).map((detail) => detail.damageType));
              tInfo.updatedTarget = this.applyDamage(tInfo.originalTarget, mitigation.total);
              tInfo.damage = {
                total: mitigation.total,
                originalTotal: dmg.total,
                type: (typesSet.size === 1 ? [...typesSet][0] : 'mixed'),
                formula: dmg.formula,
                rolls: dmg.rolls,
                resistanceApplied: mitigation.summary,
                details: mitigation.details,
                mitigatedTotal: mitigation.total
              };
              entry.hit = true;
              entry.damage = tInfo.damage;
            } else {
              entry.hit = false;
              tInfo.missed = true;
            }
            attackSpellResults.push(entry);
          }
          result.results = attackSpellResults;
        } else if (spell.healing) {
          // Healing spell: one roll per affected target
          // @date_of_change: 2025-09-19 - Added healing spell support for Cure Wounds, Healing Word, etc.
          result.affectedTargets.forEach((tInfo) => {
            const healing = this.calculateHealing(spell, false);
            tInfo.healing = healing;
            tInfo.updatedTarget = this.applyHealing(tInfo.originalTarget, healing.total);
          });
        }

        // @date_of_change: 2025-10-13 - CONDITIONS PIPELINE (structured only)
        // @date_of_change: 2025-10-14 - Apply conditions conditional on save outcomes when applicable (default: on failed save)
        if (Array.isArray(spell?.conditionsApplied) && spell.conditionsApplied.length > 0) {
          try {
            const validConditions = spell.conditionsApplied.filter((conditionKey) => (
              typeof conditionKey === 'string' && !!ConditionHandler.CONDITION_EFFECTS[conditionKey]
            ));

            if (validConditions.length > 0) {
              result.affectedTargets.forEach((tInfo) => {
                if (useSavingThrows && tInfo.saveSuccess) {
                  return;
                }

                const base = tInfo.updatedTarget || tInfo.originalTarget || {};
                const existing = Array.isArray(base.conditions) ? base.conditions.slice() : [];
                const merged = [...existing, ...validConditions];
                tInfo.updatedTarget = { ...base, conditions: merged };
                tInfo.conditionsApplied = validConditions.slice();

                validConditions.forEach((conditionKey) => {
                  try {
                    CombatLogManager.logCondition(caster.id, tInfo.targetId, conditionKey, undefined);
                  } catch (_) {}
                });
              });
            }
          } catch (condErr) {
            console.warn('[CombatManager] Condition application failed:', condErr);
          }
        }

        // @date_of_change: 2025-09-19 - Added healing effects support
        const getDamageType = (tInfo) => (
          tInfo?.damage?.details?.[0]?.damageType ||
          (Array.isArray(spell.damage) && spell.damage[0]?.type) ||
          spell.damageType ||
          'untyped'
        );

        const damageEffects = result.affectedTargets
          .filter((tInfo) => tInfo.damage && typeof tInfo.damage.total === 'number')
          .map((tInfo) => ({
            type: 'damage',
            target: tInfo.target,
            targetId: tInfo.targetId,
            damage: {
              total: tInfo.damage.total,
              type: getDamageType(tInfo),
              formula: tInfo.damage.formula,
              rolls: tInfo.damage.rolls
            }
          }));

        const healingEffects = result.affectedTargets
          .filter((tInfo) => tInfo.healing && typeof tInfo.healing.total === 'number')
          .map((tInfo) => ({
            type: 'healing',
            target: tInfo.target,
            targetId: tInfo.targetId,
            healing: {
              total: tInfo.healing.total,
              type: tInfo.healing.details?.[0]?.healingType || 'magical',
              formula: tInfo.healing.formula,
              rolls: tInfo.healing.rolls
            }
          }));

        result.effects = [...damageEffects, ...healingEffects];

        if (options?.meta?.kind === 'buff') {
          try {
            const baseCount = Number(options?.meta?.multiComponent?.baseCount) || 1;
            const perSlot = Number(options?.meta?.multiComponent?.perSlot) || 0;
            const slotLevel = Number(options?.spellLevel ?? spell?.level ?? 1) || 1;
            const maxTargets = baseCount + Math.max(0, slotLevel - 1) * perSlot;

            if (Array.isArray(potentialTargets) && potentialTargets.length > 0) {
              const selected = potentialTargets.slice(0, maxTargets);
              result.affectedTargets = selected.map((target) => ({
                target: target.name,
                targetId: target.id,
                originalTarget: target,
                distance: 0
              }));
            }

            const buffEffects = result.affectedTargets.map((tInfo) => ({
              type: 'buff',
              target: tInfo.target,
              targetId: tInfo.targetId,
              buff: {
                name: String(spell?.name || 'Buff'),
                source: caster?.name || 'Caster',
                concentration: !!(options?.meta?.requiresConcentration || spell?.requiresConcentration),
                slotLevel,
                bonuses: {
                  attackRolls: '+1d4',
                  savingThrows: '+1d4'
                }
              }
            }));

            result.effects = [...result.effects, ...buffEffects];
          } catch (buffErr) {
            console.warn('[CombatManager] Buff effect generation failed:', buffErr?.message || buffErr);
          }
        }

        result.affectedTargets.forEach((targetInfo) => {
          const hpAfter = targetInfo?.updatedTarget?.hpObj?.current ?? targetInfo?.updatedTarget?.hp;
          if (hpAfter !== null && hpAfter !== undefined && Number.isFinite(Number(hpAfter))) {
            targetInfo.targetHpAfter = Number(hpAfter);
          }
        });

        result.totalDamage = damageEffects.reduce((sum, effect) => sum + (effect.damage?.total || 0), 0);
        result.totalHealing = healingEffects.reduce((sum, effect) => sum + (effect.healing?.total || 0), 0);
      }

      console.log(`[CombatManager] Spell affected ${result.affectedTargets.length} targets`);
      return result;
  }

  /**
   * Format attack result as a readable message
   * @param {object} attackResult - The result from executeAttack
   * @returns {string} - Formatted message for display
   */
  static formatAttackMessage(attackResult) {
    try {
      if (attackResult.error) return `⚠️ Error: Failed to execute attack.`;

      let message = `${attackResult.attacker} attacks ${attackResult.target} with ${attackResult.action}:\n`;

      const { attackRoll } = attackResult;
      if (!attackRoll) return message.trim();

      const advText = attackRoll.advantage ? ' (adv)' : attackRoll.disadvantage ? ' (dis)' : '';
      const rolls = (attackRoll.rolls?.length === 2) ? ` [${attackRoll.rolls[0]}, ${attackRoll.rolls[1]}]` : '';
      message += `🎲 d20 ${attackRoll.roll}${rolls}${advText}`;

      if (attackRoll.attackBonus !== 0) {
        message += ` + ${attackRoll.attackBonus}`;
      }
      message += ` = ${attackRoll.total} vs AC ${attackRoll.targetAC}`;

      if (attackRoll.isCritical) {
        message += ` (CRITICAL HIT! 🌟)`;
      } else if (attackRoll.isCriticalFail) {
        message += ` (Critical Miss! 💫)`;
      } else if (attackRoll.isHit) {
        message += ` (Hit)`;
      } else {
        message += ` (Miss)`;
      }

      if (attackRoll.isHit && attackResult.damage) {
        message += `\n💥 Damage: `;
        const renderDetail = (d) => {
          const rolls = Array.isArray(d.rolls) ? d.rolls : [];
          let line = `${d.formula}: [${rolls.join(', ')}] = ${d.total} ${d.damageType}`;
          const notes = [];
          if (typeof d.originalTotal === 'number' && d.originalTotal !== d.total) {
            notes.push(`original ${d.originalTotal}`);
          }
          if (d.mitigation) {
            notes.push(d.mitigation);
          }
          if (notes.length > 0) {
            line += ` (${notes.join('; ')})`;
          }
          return line;
        };

        if (attackResult.damage.details.length === 1) {
          message += renderDetail(attackResult.damage.details[0]);
        } else {
          attackResult.damage.details.forEach((d, i) => {
            if (i > 0) message += `\n    + `;
            message += renderDetail(d);
          });
        }

        let totalLine = `\n    Total: ${attackResult.damage.total} damage`;
        if (typeof attackResult.damage.originalTotal === 'number' && attackResult.damage.originalTotal !== attackResult.damage.total) {
          totalLine += ` (from ${attackResult.damage.originalTotal})`;
        }
        if (attackResult.damage.resistanceApplied) {
          totalLine += `${attackResult.damage.resistanceApplied}`;
        }
        message += totalLine;

        if (attackResult.updatedTarget) {
          const hp = attackResult.updatedTarget.hpObj?.current ?? attackResult.updatedTarget.hp ?? 0;
          const max = attackResult.updatedTarget.hpObj?.max ?? attackResult.updatedTarget.maxHp ?? '?';
          const temp = attackResult.updatedTarget.hpObj?.temp ?? attackResult.updatedTarget.tempHp ?? 0;
          message += `\n❤️ ${attackResult.target}: ${hp}/${max} HP${temp ? ` (temp ${temp})` : ''}`;
        }
      }

      return message;
    } catch (error) {
      console.error('[CombatManager] Error formatting attack message:', error);
      return `${attackResult.attacker} attacks ${attackResult.target} with ${attackResult.action}`;
    }
  }

  // -----------------------
  // Skill checks and ability checks
  // -----------------------

  /**
   * Roll a skill check for a character/monster
   * @param {Object} character - Character or monster object
   * @param {string} skill - Skill name (e.g., 'stealth', 'perception', 'athletics')
   * @param {Object} modifiers - Additional modifiers { advantage, disadvantage, bonus }
   * @returns {Object} - Skill check result
   * 
   * date_of_change: 2025-09-10 - Added for ActionEconomyProcessor integration
   */
  /**
   * Roll a raw ability check without skill proficiency.
   * D&D 5e rule reference: an ability check is d20 plus the relevant ability modifier;
   * proficiency applies only when a skill or other rule explicitly grants it.
   * date_of_change: 2026-07-11
   */
  static rollAbilityCheck(character, ability, modifiers = {}) {
    const { advantage = false, disadvantage = false, bonus = 0, manualRolls = null } = modifiers;
    const normalizedAbility = String(ability || '').trim().toLowerCase();
    const abilityAliases = {
      str: 'strength',
      dex: 'dexterity',
      con: 'constitution',
      int: 'intelligence',
      wis: 'wisdom',
      cha: 'charisma'
    };
    const resolvedAbility = abilityAliases[normalizedAbility] || normalizedAbility;
    const abilityScore = character?.abilities?.[resolvedAbility] ?? character?.[resolvedAbility];
    if (!Number.isFinite(Number(abilityScore))) {
      throw new Error(`Ability check requires numeric ability score at abilities.${resolvedAbility}`);
    }

    const abilityModifier = this.getAbilityMod(Number(abilityScore));
    const requestedManualRolls = Array.isArray(manualRolls) && manualRolls.length > 0
      ? manualRolls
      : this.requestManualD20Rolls(character, { advantage, disadvantage, label: `${resolvedAbility} check` });
    const d20Roll = this.rollD20({ advantage, disadvantage, manualRolls: requestedManualRolls });
    const modifier = abilityModifier + Number(bonus || 0);
    const total = d20Roll.value + modifier;
    const modifierText = modifier >= 0 ? `+${modifier}` : `${modifier}`;

    return {
      checkType: 'ability',
      ability: resolvedAbility,
      roll: d20Roll.value,
      d20Roll: d20Roll.value,
      modifier,
      total,
      formula: `1d20${modifierText}`,
      rolls: d20Roll.rolls,
      rollType: d20Roll.rollType,
      chosenValue: d20Roll.chosenValue,
      advantage: d20Roll.advantage,
      disadvantage: d20Roll.disadvantage
    };
  }

  static rollSkillCheck(character, skill, modifiers = {}) {
    const { advantage = false, disadvantage = false, bonus = 0, manualRolls = null } = modifiers;
    
    try {
      // Get ability modifier for the skill
      const normalizedSkillText = String(skill || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
      const skillAliases = {
        'sleight of hand': 'sleightOfHand',
        'sleightofhand': 'sleightOfHand',
        'animal handling': 'animalHandling',
        'animalhandling': 'animalHandling'
      };
      const canonicalSkill = skillAliases[normalizedSkillText] || normalizedSkillText.replace(/\s+/g, '');
      const skillToAbility = {
        'athletics': 'strength',
        'acrobatics': 'dexterity',
        'sleightOfHand': 'dexterity',
        'stealth': 'dexterity',
        'arcana': 'intelligence',
        'history': 'intelligence',
        'investigation': 'intelligence',
        'nature': 'intelligence',
        'religion': 'intelligence',
        'animalHandling': 'wisdom',
        'insight': 'wisdom',
        'medicine': 'wisdom',
        'perception': 'wisdom',
        'survival': 'wisdom',
        'deception': 'charisma',
        'intimidation': 'charisma',
        'performance': 'charisma',
        'persuasion': 'charisma'
      };
      
      const ability = skillToAbility[canonicalSkill];
      if (!ability) {
        throw new Error(`${character?.id || 'unknown combatant'}/skill-check: skill must be a canonical D&D skill; received "${skill}".`);
      }
      const abilityAbbreviation = {
        strength: 'str', dexterity: 'dex', constitution: 'con',
        intelligence: 'int', wisdom: 'wis', charisma: 'cha'
      }[ability];
      const abilityScore = character.abilities?.[ability]
        ?? character.abilities?.[abilityAbbreviation]
        ?? character[ability];
      const skillEntry = Object.entries(character.skills || {}).find(([key]) => {
        const normalizedKey = String(key).trim().toLowerCase().replace(/[_\s-]+/g, '');
        return normalizedKey === canonicalSkill.toLowerCase();
      })?.[1] ?? null;

      // Canonical monster entries author the final skill bonus. Character
      // entries may instead author proficiency/expertise and a proficiency
      // bonus. Never derive proficiency from CR or inject a score at runtime.
      // rule_reference: SRD 5.1 ability checks; date_of_change: 2026-09-06.
      const explicitSkillBonus = Number(skillEntry?.bonus);
      let skillBonus;
      if (Number.isFinite(explicitSkillBonus)) {
        skillBonus = explicitSkillBonus;
      } else {
        if (!Number.isFinite(Number(abilityScore))) {
          throw new Error(`${character?.id || 'unknown combatant'}/skill-check: abilities.${ability} (or canonical ${abilityAbbreviation}) must be a finite score when skills.${canonicalSkill}.bonus is absent.`);
        }
        skillBonus = this.getAbilityMod(Number(abilityScore));
        if (skillEntry?.proficient) {
          const proficiencyBonus = Number(character.proficiencyBonus ?? character.cr?.pb);
          if (!Number.isFinite(proficiencyBonus)) {
            throw new Error(`${character?.id || 'unknown combatant'}/skill-check: proficiencyBonus or cr.pb must be finite for proficient skills.${canonicalSkill}.`);
          }
          skillBonus += proficiencyBonus;

          if (skillEntry.expertise) {
            skillBonus += proficiencyBonus;
          }
        }
      }
      
      // Add any additional bonus
      skillBonus += bonus;
      
      // date_of_change: 2026-07-11 - Preserve structured skill-check data for non-attack audit logs.
      const requestedManualRolls = Array.isArray(manualRolls) && manualRolls.length > 0
        ? manualRolls
        : this.requestManualD20Rolls(character, { advantage, disadvantage, label: `${skill} check` });
      const d20Roll = this.rollD20({ advantage, disadvantage, manualRolls: requestedManualRolls });
      const total = d20Roll.value + skillBonus;
      const modifierText = skillBonus >= 0 ? `+${skillBonus}` : `${skillBonus}`;
      
      const result = {
        checkType: 'skill',
        skill: canonicalSkill,
        ability,
        roll: d20Roll.value,
        d20Roll: d20Roll.value,
        skillBonus,
        modifier: skillBonus,
        total,
        formula: `1d20${modifierText}`,
        rolls: d20Roll.rolls,
        rollType: d20Roll.rollType,
        chosenValue: d20Roll.chosenValue,
        advantage: d20Roll.advantage,
        disadvantage: d20Roll.disadvantage,
        proficient: Boolean(skillEntry?.proficient),
        expertise: Boolean(skillEntry?.expertise)
      };
      
      console.log(`[CombatManager] ${character.name} ${canonicalSkill} check: d20(${d20Roll.value}) + ${skillBonus} = ${total}`);
      return result;
      
    } catch (error) {
      console.error('[CombatManager] Skill check error:', error);
      return {
        checkType: 'skill',
        skill,
        roll: null,
        d20Roll: null,
        modifier: null,
        total: null,
        formula: null,
        error: error.message
      };
    }
  }
}

export default CombatManager;
